import * as THREE from "three";
import type { GLTF, GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { explorerById, type ExplorerId } from "../domain/explorers";
import type {
  CompanionStyle,
  GameRegion,
  PlayerPosition,
} from "../domain/schema";

export interface WorldInteraction {
  id: string;
  label: string;
}

interface ExplorerAnimationState {
  mixer: THREE.AnimationMixer;
  idle?: THREE.AnimationAction;
  walk?: THREE.AnimationAction;
}

export type CubePetId = "bunny" | "beaver" | "crab" | "panda" | "fox";

interface CubePetAnimationState {
  mixer: THREE.AnimationMixer;
  idle?: THREE.AnimationAction;
  walk?: THREE.AnimationAction;
}

interface SanctuaryGameCallbacks {
  onInteractionAvailable(interaction?: WorldInteraction): void;
  onInteraction(id: string): void;
  onPauseChange(paused: boolean): void;
  onPositionChange(position: PlayerPosition): void;
  onCompanionPositionChange(
    position: PlayerPosition,
    distanceFromPlayer: number,
    facingYaw: number,
    visual: "licensed" | "fallback",
  ): void;
  onCastVisualChange(summary: string): void;
  onCameraChange(yaw: number): void;
  onPlayerFacingChange(yaw: number): void;
  onPerformanceSample(framesPerSecond: number): void;
  onVisualAssetsChange(status: "loaded" | "partial" | "fallback"): void;
  reducedMotion(): boolean;
}

const WORLD_RADIUS = 16;
export const PLAYER_GROUND_Y = 0.05;
export const EXPLORER_VISUAL_SCALE = 0.9;
export const GUIDE_SURFACE_Y = 0.42;
export const REGIONAL_GUIDE_GROUND_Y = 0.04;
const MAX_FRAME_DELTA = 1 / 30;
const GUIDE_POSITION = new THREE.Vector3(3.2, GUIDE_SURFACE_Y, -1.8);
const SAFARI_CENTRE_Z = -58;
const MEADOW_CENTRE_X = 58;
const COAST_CENTRE_X = -58;
const HEALTH_CENTRE_Z = 58;
const INTERACTION_RANGE = 4.5;
const INTERACTIONS: Record<
  GameRegion,
  Array<WorldInteraction & { position: THREE.Vector3 }>
> = {
  sanctuary: [
    {
      id: "chameleon-guide",
      label: "Talk to your chameleon",
      position: GUIDE_POSITION,
    },
    {
      id: "safari-portal",
      label: "Enter Skeleton Safari",
      position: new THREE.Vector3(0, PLAYER_GROUND_Y, -13.1),
    },
    {
      id: "muscle-portal",
      label: "Enter Muscle Meadow",
      position: new THREE.Vector3(13.1, PLAYER_GROUND_Y, 0),
    },
    {
      id: "coast-portal",
      label: "Enter Classification Coast",
      position: new THREE.Vector3(-13.1, PLAYER_GROUND_Y, 0),
    },
    {
      id: "health-portal",
      label: "Enter Healing Habitat",
      position: new THREE.Vector3(0, PLAYER_GROUND_Y, 13.1),
    },
  ],
  "skeleton-safari": [
    {
      id: "safari-rabbit",
      label: "Talk to Fern the rabbit",
      position: new THREE.Vector3(0, PLAYER_GROUND_Y, -53),
    },
    {
      id: "fossil-puzzle",
      label: "Investigate the fossil puzzle",
      position: new THREE.Vector3(-8, PLAYER_GROUND_Y, -58),
    },
    {
      id: "guardian-grove",
      label: "Explore Guardian Grove",
      position: new THREE.Vector3(7.5, PLAYER_GROUND_Y, -59),
    },
    {
      id: "support-bridge",
      label: "Repair Support Bridge",
      position: new THREE.Vector3(-7.5, PLAYER_GROUND_Y, -67),
    },
    {
      id: "model-glade",
      label: "Study the models",
      position: new THREE.Vector3(7.5, PLAYER_GROUND_Y, -68),
    },
    {
      id: "bone-lab",
      label: "Match bones to body parts and jobs",
      position: new THREE.Vector3(0, PLAYER_GROUND_Y, -72),
    },
    {
      id: "sanctuary-portal",
      label: "Return to the sanctuary",
      position: new THREE.Vector3(0, PLAYER_GROUND_Y, -45),
    },
  ],
  "muscle-meadow": [
    {
      id: "meadow-beaver",
      label: "Talk to Mara the beaver",
      position: new THREE.Vector3(53, PLAYER_GROUND_Y, 0),
    },
    {
      id: "pair-power",
      label: "Operate the paired-muscle arm",
      position: new THREE.Vector3(58, PLAYER_GROUND_Y, -7),
    },
    {
      id: "book-lift",
      label: "Investigate the book lift",
      position: new THREE.Vector3(65, PLAYER_GROUND_Y, -2),
    },
    {
      id: "elastic-model",
      label: "Try the elastic arm model",
      position: new THREE.Vector3(65, PLAYER_GROUND_Y, 8),
    },
    {
      id: "healthy-trail",
      label: "Plan a healthy movement trail",
      position: new THREE.Vector3(53, PLAYER_GROUND_Y, 8),
    },
    {
      id: "meadow-sanctuary-portal",
      label: "Return to the sanctuary",
      position: new THREE.Vector3(45, PLAYER_GROUND_Y, 0),
    },
  ],
  "classification-coast": [
    {
      id: "coast-crab",
      label: "Talk to Cove the crab",
      position: new THREE.Vector3(-53, PLAYER_GROUND_Y, 0),
    },
    {
      id: "backbone-beach",
      label: "Sort visitors at Backbone Beach",
      position: new THREE.Vector3(-58, PLAYER_GROUND_Y, -8),
    },
    {
      id: "armour-pools",
      label: "Observe the Armour Pools",
      position: new THREE.Vector3(-66, PLAYER_GROUND_Y, -3),
    },
    {
      id: "five-families",
      label: "Visit the Five-Group Lookout",
      position: new THREE.Vector3(-65, PLAYER_GROUND_Y, 8),
    },
    {
      id: "lighthouse-key",
      label: "Follow the Lighthouse Key",
      position: new THREE.Vector3(-55, PLAYER_GROUND_Y, 10),
    },
    {
      id: "storm-key-repair",
      label: "Repair the storm-tossed key",
      position: new THREE.Vector3(-47, PLAYER_GROUND_Y, 7),
    },
    {
      id: "coast-sanctuary-portal",
      label: "Return to the sanctuary",
      position: new THREE.Vector3(-45, PLAYER_GROUND_Y, 0),
    },
  ],
  "healing-habitat": [
    {
      id: "health-panda",
      label: "Talk to Sage the panda",
      position: new THREE.Vector3(0, PLAYER_GROUND_Y, 53),
    },
    {
      id: "trusted-care-desk",
      label: "Visit the Trusted Care Desk",
      position: new THREE.Vector3(-8, PLAYER_GROUND_Y, 58),
    },
    {
      id: "forms-and-claims",
      label: "Explore the Medicine Forms Gallery",
      position: new THREE.Vector3(7.5, PLAYER_GROUND_Y, 59),
    },
    {
      id: "gentle-germ-trail",
      label: "Trace the Gentle Germ Trail",
      position: new THREE.Vector3(-7.5, PLAYER_GROUND_Y, 67),
    },
    {
      id: "prevention-garden",
      label: "Care for the Prevention Garden",
      position: new THREE.Vector3(7.5, PLAYER_GROUND_Y, 68),
    },
    {
      id: "trustworthy-research",
      label: "Research in the Care Greenhouse",
      position: new THREE.Vector3(0, PLAYER_GROUND_Y, 72),
    },
    {
      id: "health-sanctuary-portal",
      label: "Return to the sanctuary",
      position: new THREE.Vector3(0, PLAYER_GROUND_Y, 45),
    },
  ],
};
const CUBE_PET_PATHS: Record<CubePetId, string> = {
  bunny: "/assets/kenney-cube-pets/animal-bunny.glb",
  beaver: "/assets/kenney-cube-pets/animal-beaver.glb",
  crab: "/assets/kenney-cube-pets/animal-crab.glb",
  panda: "/assets/kenney-cube-pets/animal-panda.glb",
  fox: "/assets/kenney-cube-pets/animal-fox.glb",
};
const REGION_CUBE_PET: Partial<Record<GameRegion, CubePetId>> = {
  "skeleton-safari": "bunny",
  "muscle-meadow": "beaver",
  "classification-coast": "crab",
  "healing-habitat": "panda",
};
const CUBE_PET_FALLBACK_NAMES: Record<CubePetId, string> = {
  bunny: "fern-bunny-character",
  beaver: "mara-beaver-character",
  crab: "cove-crab-character",
  panda: "sage-panda-character",
  fox: "fox-trail-companion",
};
const GUIDE_CHARACTER_NAMES = [
  "chameleon-guide-character",
  "fern-bunny-character",
  "fern-bunny-character-licensed",
  "mara-beaver-character",
  "mara-beaver-character-licensed",
  "cove-crab-character",
  "cove-crab-character-licensed",
  "sage-panda-character",
  "sage-panda-character-licensed",
] as const;
const REGIONAL_GUIDE_NAMES = GUIDE_CHARACTER_NAMES.slice(1);
const MOVE_KEYS = new Set([
  "KeyW",
  "KeyA",
  "KeyS",
  "KeyD",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
]);

export class SanctuaryGame {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
  private readonly renderer: THREE.WebGLRenderer;
  private readonly timer = new THREE.Timer();
  private readonly player = createPlayer();
  private readonly guide = createChameleon();
  private readonly spark = createKnowledgeSpark();
  private readonly foxCompanion = createFoxCompanion();
  private readonly pressed = new Set<string>();
  private readonly characterPosition = new THREE.Vector3();
  private readonly characterTarget = new THREE.Vector3();
  private readonly foxTarget = new THREE.Vector3();
  private readonly foxMovement = new THREE.Vector3();
  private playerIdleAction?: THREE.AnimationAction;
  private playerWalkAction?: THREE.AnimationAction;
  private playerMixer?: THREE.AnimationMixer;
  private readonly licensedExplorers = new Map<ExplorerId, THREE.Object3D>();
  private readonly explorerAnimations = new Map<
    ExplorerId,
    ExplorerAnimationState
  >();
  private readonly loadingExplorers = new Set<ExplorerId>();
  private explorerLoader?: GLTFLoader;
  private cubePetLoader?: GLTFLoader;
  private readonly cubePets = new Map<CubePetId, THREE.Object3D>();
  private readonly cubePetAnimations = new Map<
    CubePetId,
    CubePetAnimationState
  >();
  private readonly loadingCubePets = new Set<CubePetId>();
  private visualBaseComplete = false;
  private visualBaseAny = false;
  private explorerId: ExplorerId;
  private readonly sun = new THREE.DirectionalLight(0xffe6a6, 3.4);
  private readonly sunTarget = new THREE.Object3D();
  private readonly callbacks: SanctuaryGameCallbacks;
  private readonly resizeObserver: ResizeObserver;
  private animationFrame = 0;
  private cameraYaw = 0;
  private cameraPitch = 0.38;
  private verticalVelocity = 0;
  private grounded = true;
  private paused = false;
  private activeInteraction?: WorldInteraction;
  private region: GameRegion;
  private dragging = false;
  private pointerX = 0;
  private pointerY = 0;
  private touchForward = 0;
  private touchRight = 0;
  private lastPositionReport = 0;
  private performanceFrameCount = 0;
  private performanceWindowStart = performance.now();
  private readonly onKeyDownBound = (event: KeyboardEvent) =>
    this.onKeyDown(event);
  private readonly onKeyUpBound = (event: KeyboardEvent) => this.onKeyUp(event);
  private readonly onPointerDownBound = (event: PointerEvent) =>
    this.onPointerDown(event);
  private readonly onPointerMoveBound = (event: PointerEvent) =>
    this.onPointerMove(event);
  private readonly onPointerUpBound = (event: PointerEvent) =>
    this.onPointerUp(event);
  private readonly onInputInterruptedBound = () => {
    this.pressed.clear();
    this.clearTouchInput();
  };

  constructor(
    private readonly canvas: HTMLCanvasElement,
    callbacks: SanctuaryGameCallbacks,
    initialPosition: PlayerPosition,
    initialRegion: GameRegion,
    initialExplorer: ExplorerId,
    private readonly visualQuality: "balanced" | "high" = "high",
  ) {
    this.callbacks = callbacks;
    this.region = initialRegion;
    this.explorerId = initialExplorer;
    this.cameraYaw = initialRegion === "healing-habitat" ? Math.PI : 0;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.setPixelRatio(
      Math.min(
        window.devicePixelRatio,
        visualQuality === "balanced" ? 1.1 : 1.35,
      ),
    );

    this.scene.background = new THREE.Color(0x8edcf2);
    this.scene.fog = new THREE.Fog(0x98ddf2, 24, 52);
    this.player.position.set(
      initialPosition.x,
      PLAYER_GROUND_Y,
      initialPosition.z,
    );
    this.player.rotation.y = this.cameraYaw;
    this.guide.position.copy(GUIDE_POSITION);
    this.spark.position.set(
      GUIDE_POSITION.x,
      GUIDE_POSITION.y + 1.8,
      GUIDE_POSITION.z,
    );
    this.placeFoxAtPlayer();

    this.scene.add(this.player, this.guide, this.spark, this.foxCompanion);
    createSanctuary(this.scene);
    createSkeletonSafari(this.scene, SAFARI_CENTRE_Z);
    createMuscleMeadow(this.scene, MEADOW_CENTRE_X);
    createClassificationCoast(this.scene, COAST_CENTRE_X);
    createHealingHabitat(this.scene, HEALTH_CENTRE_Z);
    this.setRestored(false, false, false, false);
    this.updateRegionVisibility();
    this.guide.visible = initialRegion === "sanctuary";
    this.spark.visible = initialRegion === "sanctuary";
    if (initialRegion !== "sanctuary") {
      const regionColor =
        initialRegion === "skeleton-safari"
          ? 0x9fc4ba
          : initialRegion === "muscle-meadow"
            ? 0xa8d5dc
            : initialRegion === "classification-coast"
              ? 0x8fcddd
              : 0xb8ddce;
      this.scene.background = new THREE.Color(regionColor);
      if (this.scene.fog instanceof THREE.Fog)
        this.scene.fog.color.set(regionColor);
    }
    this.addLighting();
    this.markCharacterShadows(this.player);
    this.markCharacterShadows(this.guide);
    this.markCharacterShadows(this.foxCompanion);
    this.applyExplorerVisual();
    this.updateCamera();
    void this.loadVisualEnhancements().catch(() => {
      this.callbacks.onVisualAssetsChange("fallback");
    });

    window.addEventListener("keydown", this.onKeyDownBound);
    window.addEventListener("keyup", this.onKeyUpBound);
    canvas.addEventListener("pointerdown", this.onPointerDownBound);
    window.addEventListener("pointermove", this.onPointerMoveBound);
    window.addEventListener("pointerup", this.onPointerUpBound);
    window.addEventListener("blur", this.onInputInterruptedBound);
    document.addEventListener("visibilitychange", this.onInputInterruptedBound);
    this.timer.connect(document);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
    this.animate();
  }

  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    this.clearTouchInput();
    this.pressed.clear();
    this.timer.reset();
    this.callbacks.onPauseChange(paused);
  }

  setTouchMovement(forward: number, right: number): void {
    if (this.paused) return;
    this.touchForward = THREE.MathUtils.clamp(forward, -1, 1);
    this.touchRight = THREE.MathUtils.clamp(right, -1, 1);
  }

  rotateCamera(deltaX: number, deltaY: number): void {
    if (this.paused) return;
    this.cameraYaw -= deltaX * 0.007;
    this.cameraPitch = THREE.MathUtils.clamp(
      this.cameraPitch + deltaY * 0.004,
      0.12,
      0.78,
    );
    this.callbacks.onCameraChange(Number(this.cameraYaw.toFixed(3)));
    this.updateCamera();
  }

  jump(): void {
    if (this.paused || !this.grounded) return;
    this.verticalVelocity = 6.2;
    this.grounded = false;
  }

  interact(): void {
    if (this.paused || !this.activeInteraction) return;
    this.callbacks.onInteraction(this.activeInteraction.id);
  }

  clearTouchInput(): void {
    this.touchForward = 0;
    this.touchRight = 0;
    this.dragging = false;
  }

  togglePause(): void {
    this.setPaused(!this.paused);
  }

  restart(position?: PlayerPosition): void {
    const safePosition =
      position ??
      (this.region === "sanctuary"
        ? { x: 0, y: PLAYER_GROUND_Y, z: 5 }
        : this.region === "skeleton-safari"
          ? { x: 0, y: PLAYER_GROUND_Y, z: -49 }
          : this.region === "muscle-meadow"
            ? { x: 49, y: PLAYER_GROUND_Y, z: 0 }
            : this.region === "classification-coast"
              ? { x: -49, y: PLAYER_GROUND_Y, z: 0 }
              : { x: 0, y: PLAYER_GROUND_Y, z: 49 });
    this.player.position.set(safePosition.x, PLAYER_GROUND_Y, safePosition.z);
    this.verticalVelocity = 0;
    this.grounded = true;
    this.cameraYaw = this.region === "healing-habitat" ? Math.PI : 0;
    this.player.rotation.y = this.cameraYaw;
    this.cameraPitch = 0.38;
    this.placeFoxAtPlayer();
    this.reportPosition(true);
    this.updateCamera();
  }

  setRegion(region: GameRegion, position?: PlayerPosition): void {
    this.region = region;
    this.updateRegionVisibility();
    this.activeInteraction = undefined;
    this.callbacks.onInteractionAvailable(undefined);
    this.guide.visible = region === "sanctuary";
    this.spark.visible = region === "sanctuary";
    const regionColor =
      region === "sanctuary"
        ? 0x98ddf2
        : region === "skeleton-safari"
          ? 0x9fc4ba
          : region === "muscle-meadow"
            ? 0xa8d5dc
            : region === "classification-coast"
              ? 0x8fcddd
              : 0xb8ddce;
    this.scene.background = new THREE.Color(regionColor);
    if (this.scene.fog instanceof THREE.Fog)
      this.scene.fog.color.set(regionColor);
    this.restart(position);
    if (this.cubePetLoader) void this.ensureRegionCubePet();
  }

  private updateRegionVisibility(): void {
    const worlds: Array<[string, GameRegion]> = [
      ["skeleton-safari-world", "skeleton-safari"],
      ["muscle-meadow-world", "muscle-meadow"],
      ["classification-coast-world", "classification-coast"],
      ["healing-habitat-world", "healing-habitat"],
    ];
    for (const [name, region] of worlds) {
      const world = this.scene.getObjectByName(name);
      if (world) world.visible = this.region === region;
    }
  }

  setRestored(
    safariRestored: boolean,
    meadowRestored = false,
    coastRestored = false,
    healthRestored = false,
    finaleRestored = false,
  ): void {
    const safariRestoration = this.scene.getObjectByName("safari-restoration");
    const fossilGarden = this.scene.getObjectByName("sanctuary-fossil-garden");
    const meadowRestoration = this.scene.getObjectByName("meadow-restoration");
    const movementGarden = this.scene.getObjectByName(
      "sanctuary-movement-garden",
    );
    const coastRestoration = this.scene.getObjectByName("coast-restoration");
    const tidepoolObservatory = this.scene.getObjectByName(
      "sanctuary-tidepool-observatory",
    );
    const healthRestoration = this.scene.getObjectByName("health-restoration");
    const careGreenhouse = this.scene.getObjectByName(
      "sanctuary-care-greenhouse",
    );
    const celebration = this.scene.getObjectByName("sanctuary-celebration");
    if (safariRestoration) safariRestoration.visible = safariRestored;
    if (fossilGarden) fossilGarden.visible = safariRestored;
    if (meadowRestoration) meadowRestoration.visible = meadowRestored;
    if (movementGarden) movementGarden.visible = meadowRestored;
    if (coastRestoration) coastRestoration.visible = coastRestored;
    if (tidepoolObservatory) tidepoolObservatory.visible = coastRestored;
    if (healthRestoration) healthRestoration.visible = healthRestored;
    if (careGreenhouse) careGreenhouse.visible = healthRestored;
    if (celebration) celebration.visible = finaleRestored;
  }

  setCompanionStyle(style: CompanionStyle): void {
    const material = this.guide.userData.scarfMaterial as
      THREE.MeshStandardMaterial | undefined;
    if (!material) return;
    const colors: Record<CompanionStyle, number> = {
      leaf: 0x397d57,
      sunset: 0xe77868,
      ocean: 0x4d8fb1,
      starlight: 0xf3c957,
    };
    material.color.setHex(colors[style]);
    material.emissive.setHex(style === "starlight" ? 0x7c5b18 : 0x000000);
    material.emissiveIntensity = style === "starlight" ? 0.5 : 0;
  }

  setExplorer(explorerId: ExplorerId): void {
    this.explorerId = explorerId;
    this.applyExplorerVisual();
    if (this.explorerLoader) void this.loadExplorerModel(explorerId);
  }

  private applyExplorerVisual(): void {
    const explorer = explorerById(this.explorerId);
    const palettes = this.player.userData.paletteMaterials as Record<
      keyof typeof explorer.colors,
      THREE.MeshStandardMaterial[]
    >;
    for (const [part, materials] of Object.entries(palettes) as Array<
      [keyof typeof explorer.colors, THREE.MeshStandardMaterial[]]
    >) {
      for (const partMaterial of materials)
        partMaterial.color.setHex(explorer.colors[part]);
    }

    const selectedModel = this.licensedExplorers.get(this.explorerId);
    const licensedModels = new Set(this.licensedExplorers.values());
    for (const [id, model] of this.licensedExplorers)
      model.visible = id === this.explorerId;
    for (const child of this.player.children) {
      if (licensedModels.has(child)) continue;
      child.visible =
        !selectedModel ||
        child.name.startsWith("lily-pack") ||
        child.name === "lily-rounded-explorer-pack";
    }

    const animation = this.explorerAnimations.get(this.explorerId);
    this.playerMixer = animation?.mixer;
    this.playerIdleAction = animation?.idle;
    this.playerWalkAction = animation?.walk;
    this.playerIdleAction?.play().setEffectiveWeight(1);
    this.playerWalkAction?.play().setEffectiveWeight(0);
  }

  private async loadExplorerModel(explorerId: ExplorerId): Promise<boolean> {
    const existing = this.licensedExplorers.get(explorerId);
    if (existing) {
      this.applyExplorerVisual();
      return true;
    }
    if (!this.explorerLoader || this.loadingExplorers.has(explorerId))
      return false;

    this.loadingExplorers.add(explorerId);
    try {
      const definition = explorerById(explorerId);
      const asset = await this.explorerLoader.loadAsync(definition.modelPath);
      if (this.explorerId !== explorerId) return false;
      this.discardExplorerModels();

      const model = normaliseLicensedModel(asset.scene, 1.9);
      model.name = `explorer-${explorerId}-character-licensed`;
      model.rotation.y = Math.PI;
      this.player.add(model);
      this.markCharacterShadows(model);
      this.licensedExplorers.set(explorerId, model);

      const mixer = new THREE.AnimationMixer(model);
      const idle = asset.animations.find(({ name }) => name === "idle");
      const walk = asset.animations.find(({ name }) => name === "walk");
      const animation: ExplorerAnimationState = { mixer };
      if (idle) animation.idle = mixer.clipAction(idle);
      if (walk) animation.walk = mixer.clipAction(walk);
      animation.idle?.play().setEffectiveWeight(1);
      if (animation.idle) animation.idle.paused = true;
      animation.walk?.play().setEffectiveWeight(0);
      mixer.update(0);
      this.explorerAnimations.set(explorerId, animation);
      this.applyExplorerVisual();
      this.reportVisualStatus();
      return true;
    } catch {
      if (this.explorerId === explorerId) this.reportVisualStatus();
      return false;
    } finally {
      this.loadingExplorers.delete(explorerId);
    }
  }

  private discardExplorerModels(): void {
    for (const model of this.licensedExplorers.values()) {
      this.player.remove(model);
      model.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        child.geometry.dispose();
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];
        for (const childMaterial of materials) childMaterial.dispose();
      });
    }
    for (const { mixer } of this.explorerAnimations.values())
      mixer.stopAllAction();
    this.licensedExplorers.clear();
    this.explorerAnimations.clear();
    this.playerMixer = undefined;
    this.playerIdleAction = undefined;
    this.playerWalkAction = undefined;
  }

  private async loadCubePet(id: CubePetId): Promise<boolean> {
    if (this.cubePets.has(id)) return true;
    if (!this.cubePetLoader || this.loadingCubePets.has(id)) return false;
    this.loadingCubePets.add(id);
    try {
      const asset = await this.cubePetLoader.loadAsync(CUBE_PET_PATHS[id]);
      if (id !== "fox" && REGION_CUBE_PET[this.region] !== id) return false;
      const fallback = this.scene.getObjectByName(CUBE_PET_FALLBACK_NAMES[id]);
      if (!fallback?.parent) return false;
      const heights: Record<CubePetId, number> = {
        bunny: 1.55,
        beaver: 1.55,
        crab: 1.4,
        panda: 1.55,
        fox: 0.95,
      };
      const model = normaliseLicensedModel(asset.scene, heights[id]);
      model.name = `${CUBE_PET_FALLBACK_NAMES[id]}-licensed`;
      model.userData.frontAxis = "positive-z";
      model.position.copy(fallback.position);
      model.rotation.copy(fallback.rotation);
      fallback.visible = false;
      fallback.parent.add(model);
      this.markCharacterShadows(model);

      const mixer = new THREE.AnimationMixer(model);
      const idle = asset.animations.find(({ name }) => name === "idle");
      const walk = asset.animations.find(({ name }) => name === "walk");
      const animation: CubePetAnimationState = { mixer };
      if (idle) animation.idle = mixer.clipAction(idle);
      if (walk) animation.walk = mixer.clipAction(walk);
      animation.idle?.play().setEffectiveWeight(1);
      animation.walk?.play().setEffectiveWeight(0);
      mixer.update(0);
      this.cubePets.set(id, model);
      this.cubePetAnimations.set(id, animation);
      this.reportVisualStatus();
      return true;
    } catch {
      this.reportVisualStatus();
      return false;
    } finally {
      this.loadingCubePets.delete(id);
    }
  }

  private discardCubePet(id: CubePetId): void {
    const model = this.cubePets.get(id);
    if (model) {
      model.parent?.remove(model);
      model.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        child.geometry.dispose();
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];
        for (const childMaterial of materials) childMaterial.dispose();
      });
    }
    this.cubePetAnimations.get(id)?.mixer.stopAllAction();
    this.cubePets.delete(id);
    this.cubePetAnimations.delete(id);
    const fallback = this.scene.getObjectByName(CUBE_PET_FALLBACK_NAMES[id]);
    if (fallback) fallback.visible = true;
  }

  private async ensureRegionCubePet(): Promise<boolean> {
    const expected = REGION_CUBE_PET[this.region];
    for (const id of ["bunny", "beaver", "crab", "panda"] as const) {
      if (id !== expected) this.discardCubePet(id);
    }
    const loaded = expected ? await this.loadCubePet(expected) : true;
    this.reportVisualStatus();
    return loaded;
  }

  private reportVisualStatus(): void {
    const regionPet = REGION_CUBE_PET[this.region];
    const complete =
      this.visualBaseComplete &&
      this.licensedExplorers.has(this.explorerId) &&
      this.cubePets.has("fox") &&
      (!regionPet || this.cubePets.has(regionPet));
    const any =
      this.visualBaseAny ||
      this.licensedExplorers.size > 0 ||
      this.cubePets.size > 0;
    this.callbacks.onVisualAssetsChange(
      complete ? "loaded" : any ? "partial" : "fallback",
    );
    this.callbacks.onCastVisualChange(
      ["fox", regionPet]
        .filter((id): id is CubePetId => Boolean(id))
        .map((id) => `${id}:${this.cubePets.has(id) ? "licensed" : "fallback"}`)
        .join(","),
    );
  }

  private placeFoxAtPlayer(): void {
    this.foxCompanion.position.set(
      this.player.position.x + 1.25,
      REGIONAL_GUIDE_GROUND_Y,
      this.player.position.z + 0.7,
    );
    const licensedFox = this.cubePets.get("fox");
    if (licensedFox) licensedFox.position.copy(this.foxCompanion.position);
  }

  focus(): void {
    this.canvas.focus();
  }

  dispose(): void {
    cancelAnimationFrame(this.animationFrame);
    this.resizeObserver.disconnect();
    window.removeEventListener("keydown", this.onKeyDownBound);
    window.removeEventListener("keyup", this.onKeyUpBound);
    this.canvas.removeEventListener("pointerdown", this.onPointerDownBound);
    window.removeEventListener("pointermove", this.onPointerMoveBound);
    window.removeEventListener("pointerup", this.onPointerUpBound);
    window.removeEventListener("blur", this.onInputInterruptedBound);
    document.removeEventListener(
      "visibilitychange",
      this.onInputInterruptedBound,
    );
    this.timer.dispose();
    this.discardExplorerModels();
    for (const id of ["bunny", "beaver", "crab", "panda", "fox"] as const)
      this.discardCubePet(id);
    this.renderer.dispose();
  }

  private async loadVisualEnhancements(): Promise<void> {
    const { GLTFLoader } = await import("three/addons/loaders/GLTFLoader.js");
    const loader = new GLTFLoader();
    const paths: Record<string, string> = {
      treeTall: "/assets/kenney-nature-kit/tree_tall.glb",
      treeThin: "/assets/kenney-nature-kit/tree_thin.glb",
      pine: "/assets/kenney-nature-kit/tree_pineRoundA.glb",
      rockLarge: "/assets/kenney-nature-kit/rock_largeC.glb",
      rockFlat: "/assets/kenney-nature-kit/rock_smallFlatA.glb",
      grass: "/assets/kenney-nature-kit/grass_leafsLarge.glb",
      mushroom: "/assets/kenney-nature-kit/mushroom_redGroup.glb",
      flower: "/assets/kenney-nature-kit/flower_yellowB.glb",
      log: "/assets/kenney-nature-kit/log.glb",
      tent: "/assets/kenney-nature-kit/tent_detailedOpen.glb",
    };
    const results = await Promise.allSettled(
      Object.entries(paths).map(
        async ([key, path]) => [key, await loader.loadAsync(path)] as const,
      ),
    );
    const loaded = new Map<string, GLTF>();
    for (const result of results) {
      if (result.status === "fulfilled") loaded.set(...result.value);
    }
    this.visualBaseAny = loaded.size > 0;
    this.visualBaseComplete = loaded.size === Object.keys(paths).length;

    const sanctuaryNature = new THREE.Group();
    sanctuaryNature.name = "licensed-sanctuary-nature";
    const safariNature = new THREE.Group();
    safariNature.name = "licensed-safari-nature";
    const safariWorld = this.scene.getObjectByName("skeleton-safari-world");
    this.scene.add(sanctuaryNature);
    safariWorld?.add(safariNature);

    const place = (
      parent: THREE.Group,
      key: string,
      x: number,
      z: number,
      height: number,
      rotation = 0,
    ) => {
      const asset = loaded.get(key);
      if (!asset) return;
      const model = normaliseLicensedModel(asset.scene.clone(true), height);
      model.position.set(x, 0, z);
      model.rotation.y = rotation;
      parent.add(model);
    };

    if (loaded.has("treeTall") && loaded.has("treeThin")) {
      for (const child of this.scene.children) {
        if (child.name.startsWith("sanctuary-procedural-tree-"))
          child.visible = false;
      }
    }
    for (const [key, x, z, height, rotation] of [
      ["treeTall", -10, -6, 5.3, 0.2],
      ["treeThin", -12.4, 4, 5.8, -0.4],
      ["treeTall", -7, 10.5, 4.8, 1.1],
      ["treeThin", 7.5, 10, 5.4, 0.5],
      ["treeTall", 12, 3.8, 5.6, -0.7],
      ["treeThin", 10, -8, 5.2, 0.9],
      ["treeTall", -5, -11.5, 4.7, 0.3],
      ["treeThin", 5.5, -12, 4.9, -1],
      ["rockLarge", 8.2, -6.2, 1.0, 0.3],
      ["rockFlat", -7.8, -6.3, 0.48, 1.2],
      ["mushroom", -6.8, -5.4, 0.68, 0.7],
      ["flower", 7.2, -7.2, 0.68, 0],
    ] as Array<[string, number, number, number, number]>)
      place(sanctuaryNature, key, x, z, height, rotation);

    if (
      loaded.has("pine") &&
      loaded.has("treeTall") &&
      loaded.has("treeThin")
    ) {
      for (const child of safariWorld?.children ?? []) {
        if (child.name.startsWith("safari-procedural-tree-"))
          child.visible = false;
      }
    }
    for (const [key, x, z, height, rotation] of [
      ["pine", -13, -8, 6, 0.2],
      ["pine", -15, -1, 5.5, -0.4],
      ["treeThin", -13, 7, 5.8, 0.6],
      ["pine", -8, 13, 5.4, 1],
      ["treeTall", 7, 14, 5.3, -0.7],
      ["pine", 13, 9, 6.2, 0.3],
      ["treeThin", 15, 2, 5.5, -0.8],
      ["pine", 13, -7, 5.9, 0.5],
      ["treeTall", 8, -14, 5.2, 0.9],
      ["treeThin", -7, -14, 5.1, -0.2],
      ["tent", 10.8, -8.8, 2.1, -0.7],
      ["log", -11, 8.5, 0.72, 1.2],
      ["rockLarge", 12.5, 10, 0.95, 0.4],
      ["grass", -9.4, -10.8, 0.9, 0],
      ["mushroom", 8.2, 7, 0.68, 0.6],
      ["flower", -6.5, -8.4, 0.66, 0],
    ] as Array<[string, number, number, number, number]>)
      place(safariNature, key, x, z, height, rotation);

    this.explorerLoader = loader;
    this.cubePetLoader = loader;
    await Promise.all([
      this.loadExplorerModel(this.explorerId),
      this.loadCubePet("fox"),
      this.ensureRegionCubePet(),
    ]);
    this.reportVisualStatus();
  }

  private markCharacterShadows(object: THREE.Object3D): void {
    object.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      child.castShadow = true;
      child.receiveShadow = true;
    });
  }

  private addLighting(): void {
    const sky = new THREE.HemisphereLight(0xfff7df, 0x294f49, 2.15);
    const fill = new THREE.DirectionalLight(0x91c9ff, 0.75);
    fill.position.set(-12, 8, -9);
    this.sun.position.set(9, 15, 8);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(
      this.visualQuality === "balanced" ? 1024 : 2048,
      this.visualQuality === "balanced" ? 1024 : 2048,
    );
    const shadowCamera = this.sun.shadow.camera;
    shadowCamera.left = -19;
    shadowCamera.right = 19;
    shadowCamera.top = 19;
    shadowCamera.bottom = -19;
    shadowCamera.near = 1;
    shadowCamera.far = 48;
    this.sun.shadow.bias = -0.0004;
    this.scene.add(sky, fill, this.sun, this.sunTarget);
    this.sun.target = this.sunTarget;
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.code === "Escape") {
      event.preventDefault();
      this.togglePause();
      return;
    }
    if (this.paused) return;

    if (MOVE_KEYS.has(event.code) || event.code === "Space") {
      event.preventDefault();
    }
    this.pressed.add(event.code);

    if (event.code === "Space") this.jump();
    if (event.code === "KeyE") this.interact();
  }

  private onKeyUp(event: KeyboardEvent): void {
    this.pressed.delete(event.code);
  }

  private onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || this.paused) return;
    this.dragging = true;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.canvas.setPointerCapture?.(event.pointerId);
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.dragging || this.paused) return;
    const deltaX = event.clientX - this.pointerX;
    const deltaY = event.clientY - this.pointerY;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.rotateCamera(deltaX, deltaY);
  }

  private onPointerUp(event: PointerEvent): void {
    this.dragging = false;
    this.canvas.releasePointerCapture?.(event.pointerId);
  }

  private resize(): void {
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  private animate = (timestamp?: number): void => {
    this.animationFrame = requestAnimationFrame(this.animate);
    this.timer.update(timestamp);
    const delta = Math.min(this.timer.getDelta(), MAX_FRAME_DELTA);
    if (!this.paused) this.update(delta);
    this.renderer.render(this.scene, this.camera);
    this.samplePerformance();
  };

  private samplePerformance(): void {
    this.performanceFrameCount += 1;
    const now = performance.now();
    const duration = now - this.performanceWindowStart;
    if (duration < 1_000) return;
    this.callbacks.onPerformanceSample(
      Math.round((this.performanceFrameCount * 1_000) / duration),
    );
    this.performanceFrameCount = 0;
    this.performanceWindowStart = now;
  }

  private update(delta: number): void {
    const forwardInput = THREE.MathUtils.clamp(
      Number(this.pressed.has("KeyW") || this.pressed.has("ArrowUp")) -
        Number(this.pressed.has("KeyS") || this.pressed.has("ArrowDown")) +
        this.touchForward,
      -1,
      1,
    );
    const rightInput = THREE.MathUtils.clamp(
      Number(this.pressed.has("KeyD") || this.pressed.has("ArrowRight")) -
        Number(this.pressed.has("KeyA") || this.pressed.has("ArrowLeft")) +
        this.touchRight,
      -1,
      1,
    );

    const movement = new THREE.Vector3();
    if (forwardInput || rightInput) {
      const forward = new THREE.Vector3(
        -Math.sin(this.cameraYaw),
        0,
        -Math.cos(this.cameraYaw),
      );
      const right = new THREE.Vector3(
        Math.cos(this.cameraYaw),
        0,
        -Math.sin(this.cameraYaw),
      );
      movement
        .addScaledVector(forward, forwardInput)
        .addScaledVector(right, rightInput)
        .normalize();
      this.player.position.addScaledVector(movement, 5.2 * delta);
      this.player.rotation.y = Math.atan2(-movement.x, -movement.z);
      this.callbacks.onPlayerFacingChange(
        Number(this.player.rotation.y.toFixed(3)),
      );
    }

    this.verticalVelocity -= 15 * delta;
    this.player.position.y += this.verticalVelocity * delta;
    if (this.player.position.y <= PLAYER_GROUND_Y) {
      this.player.position.y = PLAYER_GROUND_Y;
      this.verticalVelocity = 0;
      this.grounded = true;
    }

    const centreX =
      this.region === "muscle-meadow"
        ? MEADOW_CENTRE_X
        : this.region === "classification-coast"
          ? COAST_CENTRE_X
          : 0;
    const centreZ =
      this.region === "skeleton-safari"
        ? SAFARI_CENTRE_Z
        : this.region === "healing-habitat"
          ? HEALTH_CENTRE_Z
          : 0;
    const relativeX = this.player.position.x - centreX;
    const relativeZ = this.player.position.z - centreZ;
    const distanceFromCentre = Math.hypot(relativeX, relativeZ);
    if (distanceFromCentre > WORLD_RADIUS) {
      const scale = WORLD_RADIUS / distanceFromCentre;
      this.player.position.x = centreX + relativeX * scale;
      this.player.position.z = centreZ + relativeZ * scale;
    }
    if (this.player.position.y < -4) this.restart();

    const nearest = INTERACTIONS[this.region]
      .map((interaction) => ({
        interaction,
        distance: this.player.position.distanceTo(interaction.position),
      }))
      .filter(({ distance }) => distance < INTERACTION_RANGE)
      .sort((a, b) => a.distance - b.distance)[0]?.interaction;
    if (nearest?.id !== this.activeInteraction?.id) {
      this.activeInteraction = nearest;
      this.callbacks.onInteractionAvailable(nearest);
    }

    const elapsed = this.timer.getElapsed();
    const reduced = this.callbacks.reducedMotion();
    const walking = movement.lengthSq() > 0;
    this.playerIdleAction?.setEffectiveWeight(walking ? 0 : 1);
    this.playerWalkAction
      ?.setEffectiveWeight(walking && !reduced ? 1 : 0)
      .setEffectiveTimeScale(1);
    if (this.playerMixer) this.playerMixer.update(reduced ? 0 : delta);
    const selectedExplorer = this.licensedExplorers.get(this.explorerId);
    if (selectedExplorer) {
      selectedExplorer.position.y =
        !walking && !reduced ? Math.sin(elapsed * 1.1) * 0.006 : 0;
      selectedExplorer.rotation.z =
        !walking && !reduced ? Math.sin(elapsed * 0.8) * 0.004 : 0;
    }

    const fox = this.cubePets.get("fox") ?? this.foxCompanion;
    this.foxTarget
      .set(1.25, 0, 0.7)
      .applyAxisAngle(THREE.Object3D.DEFAULT_UP, this.player.rotation.y)
      .add(this.player.position);
    this.foxTarget.y = REGIONAL_GUIDE_GROUND_Y;
    this.foxMovement.subVectors(this.foxTarget, fox.position);
    const foxDistance = this.foxMovement.length();
    if (foxDistance > 6) fox.position.copy(this.foxTarget);
    else if (foxDistance > 0.08)
      fox.position.lerp(this.foxTarget, 1 - Math.exp(-5 * delta));
    const foxWalking = foxDistance > 0.16;
    if (foxWalking)
      fox.rotation.y = Math.atan2(this.foxMovement.x, this.foxMovement.z);
    const foxAnimation = this.cubePetAnimations.get("fox");
    foxAnimation?.idle?.setEffectiveWeight(foxWalking ? 0 : 1);
    foxAnimation?.walk?.setEffectiveWeight(foxWalking && !reduced ? 1 : 0);
    if (foxAnimation && !reduced) foxAnimation.mixer.update(delta);
    for (const [id, animation] of this.cubePetAnimations) {
      if (id !== "fox" && !reduced) animation.mixer.update(delta);
    }
    const fallbackTail = this.foxCompanion.userData.tail as
      THREE.Object3D | undefined;
    if (fallbackTail)
      fallbackTail.rotation.z =
        !reduced && foxWalking ? 0.9 + Math.sin(elapsed * 8) * 0.12 : 0.9;

    this.sun.position.set(
      this.player.position.x + 9,
      this.player.position.y + 15,
      this.player.position.z + 8,
    );
    this.sunTarget.position.copy(this.player.position);
    this.sunTarget.updateMatrixWorld();
    const limbs = this.player.userData.limbs as THREE.Object3D[];
    limbs.forEach((limb, index) => {
      const direction = index % 2 === 0 ? 1 : -1;
      const target = walking ? Math.sin(elapsed * 10) * 0.48 * direction : 0;
      limb.rotation.x = THREE.MathUtils.lerp(limb.rotation.x, target, 0.22);
    });
    const scarf = this.player.userData.scarf as THREE.Object3D;
    scarf.rotation.x = walking ? -0.42 + Math.sin(elapsed * 8) * 0.08 : -0.18;

    for (const name of GUIDE_CHARACTER_NAMES) {
      const character = this.scene.getObjectByName(name);
      if (!character?.visible || character.parent?.visible === false) continue;
      character.getWorldPosition(this.characterPosition);
      this.characterTarget.copy(this.player.position);
      this.characterTarget.y = this.characterPosition.y;
      character.lookAt(this.characterTarget);
      if (character.userData.frontAxis !== "positive-z")
        character.rotateY(Math.PI);
    }

    if (!reduced) {
      this.guide.position.y = GUIDE_POSITION.y + Math.sin(elapsed * 2) * 0.035;
      this.guide.rotation.z = Math.sin(elapsed * 1.4) * 0.025;
      this.spark.position.y =
        GUIDE_POSITION.y + 1.8 + Math.sin(elapsed * 2.4) * 0.12;
      this.spark.rotation.y += delta * 0.9;
      for (const [index, name] of REGIONAL_GUIDE_NAMES.entries()) {
        const character = this.scene.getObjectByName(name);
        if (character)
          character.rotation.z = Math.sin(elapsed * 1.35 + index) * 0.018;
      }
    } else {
      this.guide.rotation.z = 0;
      for (const name of REGIONAL_GUIDE_NAMES) {
        const character = this.scene.getObjectByName(name);
        if (character) character.rotation.z = 0;
      }
    }
    this.updateCamera();
    this.reportPosition(false);
  }

  private reportPosition(force: boolean): void {
    const now = performance.now();
    if (!force && now - this.lastPositionReport < 200) return;
    this.lastPositionReport = now;
    this.callbacks.onPositionChange({
      x: Number(this.player.position.x.toFixed(3)),
      y: Number(this.player.position.y.toFixed(3)),
      z: Number(this.player.position.z.toFixed(3)),
    });
    const fox = this.cubePets.get("fox") ?? this.foxCompanion;
    this.callbacks.onCompanionPositionChange(
      {
        x: Number(fox.position.x.toFixed(3)),
        y: Number(fox.position.y.toFixed(3)),
        z: Number(fox.position.z.toFixed(3)),
      },
      Number(fox.position.distanceTo(this.player.position).toFixed(3)),
      Number(fox.rotation.y.toFixed(3)),
      this.cubePets.has("fox") ? "licensed" : "fallback",
    );
  }

  private updateCamera(): void {
    const target = this.player.position
      .clone()
      .add(new THREE.Vector3(0, 1.05, 0));
    const distance = 7.2;
    const horizontal = Math.cos(this.cameraPitch) * distance;
    this.camera.position.set(
      target.x + Math.sin(this.cameraYaw) * horizontal,
      target.y + Math.sin(this.cameraPitch) * distance + 1.1,
      target.z + Math.cos(this.cameraYaw) * horizontal,
    );
    this.camera.lookAt(target);
  }
}

function normaliseLicensedModel(
  source: THREE.Object3D,
  targetHeight: number,
): THREE.Group {
  const wrapper = new THREE.Group();
  const bounds = new THREE.Box3().setFromObject(source);
  const size = bounds.getSize(new THREE.Vector3());
  const centre = bounds.getCenter(new THREE.Vector3());
  const scale = targetHeight / Math.max(size.y, 0.001);
  source.scale.setScalar(scale);
  source.position.set(
    -centre.x * scale,
    -bounds.min.y * scale,
    -centre.z * scale,
  );
  source.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.castShadow = true;
    child.receiveShadow = true;
    if (child instanceof THREE.SkinnedMesh) child.frustumCulled = false;
    if (child.material instanceof THREE.MeshStandardMaterial) {
      child.material = child.material.clone();
      child.material.roughness = Math.min(child.material.roughness, 0.82);
    }
  });
  wrapper.add(source);
  return wrapper;
}

function material(color: number, roughness = 0.8): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness });
}

function mesh(
  geometry: THREE.BufferGeometry,
  color: number,
  roughness?: number,
): THREE.Mesh {
  return new THREE.Mesh(geometry, material(color, roughness));
}

function createPlayer(): THREE.Group {
  const player = new THREE.Group();
  player.name = "lily-avatar";
  player.scale.setScalar(EXPLORER_VISUAL_SCALE);
  const skin = 0xb96f45;
  const jacket = 0x35a7a0;
  const body = mesh(new THREE.CapsuleGeometry(0.34, 0.55, 5, 10), jacket);
  body.position.y = 0.94;

  const head = mesh(new THREE.SphereGeometry(0.34, 16, 12), skin);
  head.position.y = 1.58;
  const hair = mesh(new THREE.SphereGeometry(0.355, 12, 8), 0x402820);
  hair.scale.set(1, 0.56, 1.04);
  hair.position.set(0, 1.8, 0.04);
  const fringe = mesh(new THREE.ConeGeometry(0.16, 0.28, 6), 0x402820);
  fringe.rotation.x = -0.7;
  fringe.position.set(-0.16, 1.72, -0.28);
  const ponytail = mesh(new THREE.CapsuleGeometry(0.16, 0.52, 5, 9), 0x402820);
  ponytail.position.set(0, 1.34, 0.38);
  ponytail.rotation.x = -0.12;
  const hairTie = mesh(new THREE.TorusGeometry(0.12, 0.045, 6, 10), 0xe95786);
  hairTie.rotation.x = Math.PI / 2;
  hairTie.position.set(0, 1.64, 0.34);
  const sideLocks: THREE.Mesh[] = [];
  for (const x of [-0.29, 0.29]) {
    const lock = mesh(new THREE.CapsuleGeometry(0.065, 0.32, 4, 7), 0x402820);
    lock.position.set(x, 1.4, 0.03);
    lock.rotation.z = x < 0 ? -0.1 : 0.1;
    sideLocks.push(lock);
  }

  const backpack = mesh(
    new THREE.CapsuleGeometry(0.27, 0.34, 7, 12),
    0x65449a,
    0.62,
  );
  backpack.name = "lily-rounded-explorer-pack";
  backpack.scale.z = 0.5;
  backpack.position.set(0, 1.04, 0.43);
  const packFlap = mesh(new THREE.SphereGeometry(0.25, 14, 9), 0x8e6bc2);
  packFlap.name = "lily-pack-flap";
  packFlap.scale.set(1, 0.58, 0.25);
  packFlap.position.set(0, 1.2, 0.58);
  const pocket = mesh(new THREE.CapsuleGeometry(0.16, 0.16, 5, 9), 0xa98bd2);
  pocket.name = "lily-pack-pocket";
  pocket.scale.z = 0.32;
  pocket.position.set(0, 0.92, 0.58);
  const packBadge = mesh(
    new THREE.CylinderGeometry(0.075, 0.075, 0.025, 12),
    0xffd95a,
  );
  packBadge.name = "lily-pack-sun-badge";
  packBadge.rotation.x = Math.PI / 2;
  packBadge.position.set(0, 1.21, 0.66);

  const eyes: THREE.Object3D[] = [];
  for (const x of [-0.12, 0.12]) {
    const white = mesh(new THREE.SphereGeometry(0.062, 12, 8), 0xfffbec);
    white.position.set(x, 1.62, -0.315);
    const iris = mesh(new THREE.SphereGeometry(0.038, 10, 7), 0x3f775f);
    iris.position.set(x, 1.618, -0.365);
    const pupil = mesh(new THREE.SphereGeometry(0.021, 8, 6), 0x182323);
    pupil.position.set(x, 1.618, -0.394);
    const glint = mesh(new THREE.SphereGeometry(0.009, 6, 5), 0xffffff);
    glint.position.set(x - 0.009, 1.633, -0.412);
    const brow = mesh(new THREE.CapsuleGeometry(0.014, 0.09, 3, 6), 0x402820);
    brow.rotation.z = Math.PI / 2 + (x < 0 ? -0.1 : 0.1);
    brow.position.set(x, 1.71, -0.35);
    eyes.push(white, iris, pupil, glint, brow);
  }
  const nose = mesh(new THREE.SphereGeometry(0.027, 8, 6), 0xa95f40);
  nose.name = "lily-nose";
  nose.scale.set(0.72, 1, 0.6);
  nose.position.set(0, 1.565, -0.355);
  const cheeks: THREE.Mesh[] = [];
  for (const x of [-0.2, 0.2]) {
    const cheek = mesh(new THREE.SphereGeometry(0.045, 8, 6), 0xdd806c);
    cheek.scale.set(1, 0.45, 0.3);
    cheek.position.set(x, 1.53, -0.33);
    cheeks.push(cheek);
  }
  const smile = mesh(
    new THREE.TorusGeometry(0.08, 0.016, 5, 10, Math.PI),
    0x6d352d,
  );
  smile.rotation.set(0, 0, Math.PI);
  smile.position.set(0, 1.49, -0.335);

  const skinMaterials = [head.material as THREE.MeshStandardMaterial];
  const hairMaterials = [
    hair.material,
    fringe.material,
    ponytail.material,
    ...sideLocks.map((lock) => lock.material),
  ] as THREE.MeshStandardMaterial[];
  const jacketMaterials = [body.material] as THREE.MeshStandardMaterial[];
  const trouserMaterials: THREE.MeshStandardMaterial[] = [];
  const limbs: THREE.Object3D[] = [];
  for (const side of [-1, 1]) {
    const arm = mesh(new THREE.CapsuleGeometry(0.085, 0.43, 4, 7), skin);
    arm.position.set(side * 0.43, 1.04, 0);
    arm.rotation.z = side * -0.08;
    const sleeve = mesh(new THREE.CylinderGeometry(0.12, 0.1, 0.29, 8), jacket);
    sleeve.position.set(side * 0.4, 1.25, 0);
    sleeve.rotation.z = side * -0.08;
    const leg = mesh(new THREE.CapsuleGeometry(0.11, 0.46, 4, 7), 0x315e83);
    leg.position.set(side * 0.19, 0.35, 0);
    const shoe = mesh(new THREE.SphereGeometry(0.145, 8, 6), 0xf7f0df);
    shoe.scale.set(0.8, 0.62, 1.35);
    shoe.position.set(side * 0.19, 0.08, -0.08);
    limbs.push(arm, leg);
    skinMaterials.push(arm.material as THREE.MeshStandardMaterial);
    jacketMaterials.push(sleeve.material as THREE.MeshStandardMaterial);
    trouserMaterials.push(leg.material as THREE.MeshStandardMaterial);
    player.add(arm, sleeve, leg, shoe);
  }

  const tunic = mesh(new THREE.CylinderGeometry(0.4, 0.47, 0.3, 10), 0x7250a6);
  tunic.position.y = 0.72;
  const shirt = mesh(new THREE.CylinderGeometry(0.24, 0.3, 0.32, 10), 0xffe7bb);
  shirt.name = "lily-sun-shirt";
  shirt.position.set(0, 1.2, -0.27);
  shirt.scale.z = 0.35;
  const collarLeft = mesh(new THREE.ConeGeometry(0.1, 0.2, 3), 0xfffbec);
  collarLeft.name = "lily-jacket-collar";
  collarLeft.rotation.z = 0.45;
  collarLeft.position.set(-0.09, 1.36, -0.34);
  const collarRight = collarLeft.clone();
  collarRight.rotation.z = -0.45;
  collarRight.position.x = 0.09;
  const zipper = mesh(new THREE.CapsuleGeometry(0.012, 0.34, 3, 5), 0xf2cf68);
  zipper.position.set(0, 1.08, -0.345);
  const scarf = mesh(new THREE.ConeGeometry(0.12, 0.72, 5), 0xe95786);
  scarf.name = "lily-scarf";
  scarf.position.set(0.18, 1.35, 0.48);
  scarf.rotation.x = -0.18;
  player.userData.limbs = limbs;
  player.userData.scarf = scarf;
  player.userData.paletteMaterials = {
    skin: skinMaterials,
    hair: hairMaterials,
    jacket: jacketMaterials,
    trousers: trouserMaterials,
    pack: [backpack.material as THREE.MeshStandardMaterial],
    packLight: [
      packFlap.material as THREE.MeshStandardMaterial,
      pocket.material as THREE.MeshStandardMaterial,
    ],
    scarf: [scarf.material as THREE.MeshStandardMaterial],
  };
  player.add(
    body,
    head,
    hair,
    fringe,
    ponytail,
    hairTie,
    ...sideLocks,
    backpack,
    packFlap,
    pocket,
    packBadge,
    smile,
    nose,
    ...cheeks,
    tunic,
    shirt,
    collarLeft,
    collarRight,
    zipper,
    scarf,
    createBlobShadow(0.72, 0.42, 0),
    ...eyes,
  );
  return player;
}

function createFoxCompanion(): THREE.Group {
  const fox = new THREE.Group();
  fox.name = "fox-trail-companion";
  fox.userData.frontAxis = "positive-z";
  const orange = 0xd86f38;
  const cream = 0xffe0b5;
  const dark = 0x3f302b;
  const body = mesh(new THREE.SphereGeometry(0.34, 12, 9), orange, 0.88);
  body.scale.set(1.15, 0.82, 1.35);
  body.position.y = 0.38;
  const head = mesh(new THREE.SphereGeometry(0.32, 12, 9), orange, 0.85);
  head.position.set(0, 0.67, 0.34);
  const muzzle = mesh(new THREE.SphereGeometry(0.2, 10, 8), cream);
  muzzle.scale.set(0.95, 0.65, 1.1);
  muzzle.position.set(0, 0.59, 0.62);
  const nose = mesh(new THREE.SphereGeometry(0.055, 8, 6), dark);
  nose.position.set(0, 0.62, 0.81);
  fox.add(body, head, muzzle, nose, createBlobShadow(0.48, 0.42, 0.01));
  for (const side of [-1, 1]) {
    const ear = mesh(new THREE.ConeGeometry(0.13, 0.3, 7), orange);
    ear.position.set(side * 0.2, 1, 0.3);
    const eye = mesh(new THREE.SphereGeometry(0.05, 8, 6), dark);
    eye.position.set(side * 0.13, 0.73, 0.62);
    const foot = mesh(new THREE.SphereGeometry(0.1, 8, 6), dark);
    foot.scale.set(0.8, 0.55, 1.2);
    foot.position.set(side * 0.2, 0.07, 0.14);
    fox.add(ear, eye, foot);
  }
  const tail = mesh(new THREE.CapsuleGeometry(0.14, 0.72, 5, 9), orange);
  tail.rotation.z = 0.9;
  tail.position.set(-0.48, 0.42, -0.28);
  fox.userData.tail = tail;
  fox.add(tail);
  return fox;
}

function createChameleon(): THREE.Group {
  const guide = new THREE.Group();
  guide.name = "chameleon-guide-character";
  const green = 0x52b85f;
  const lime = 0xb8df59;
  const turquoise = 0x39a991;
  const body = mesh(new THREE.SphereGeometry(0.62, 18, 12), green, 0.68);
  body.scale.set(1.48, 0.78, 0.82);
  body.position.y = 0.58;
  const belly = mesh(new THREE.SphereGeometry(0.5, 16, 10), lime, 0.72);
  belly.scale.set(1.25, 0.46, 0.73);
  belly.position.set(0, 0.43, -0.26);

  const head = mesh(new THREE.SphereGeometry(0.46, 16, 11), lime, 0.68);
  head.scale.set(1, 1.05, 1.12);
  head.position.set(0, 0.76, -0.72);
  const snout = mesh(new THREE.SphereGeometry(0.28, 12, 8), 0x8bd064);
  snout.scale.set(1.2, 0.62, 0.9);
  snout.position.set(0, 0.62, -1.06);
  const smile = mesh(
    new THREE.TorusGeometry(0.13, 0.018, 5, 12, Math.PI),
    0x285b45,
  );
  smile.rotation.set(0, 0, Math.PI);
  smile.position.set(0, 0.56, -1.28);

  for (const x of [-0.27, 0.27]) {
    const turret = mesh(new THREE.SphereGeometry(0.18, 12, 8), 0xe8ef96);
    turret.position.set(x, 1.06, -0.84);
    const pupil = mesh(new THREE.SphereGeometry(0.074, 9, 7), 0x172c2b);
    pupil.position.set(x, 1.07, -0.99);
    const glint = mesh(new THREE.SphereGeometry(0.022, 6, 5), 0xffffff);
    glint.position.set(x - 0.018, 1.095, -1.057);
    const brow = mesh(new THREE.CapsuleGeometry(0.014, 0.13, 3, 6), 0x2c7053);
    brow.rotation.z = Math.PI / 2 + (x < 0 ? -0.13 : 0.13);
    brow.position.set(x, 1.2, -0.93);
    guide.add(turret, pupil, glint, brow);
  }
  const nostrils: THREE.Mesh[] = [];
  for (const x of [-0.1, 0.1]) {
    const nostril = mesh(new THREE.SphereGeometry(0.024, 7, 5), 0x285b45);
    nostril.position.set(x, 0.69, -1.27);
    nostrils.push(nostril);
  }
  const throat = mesh(new THREE.SphereGeometry(0.28, 12, 8), 0xf0d96e);
  throat.name = "chameleon-golden-throat";
  throat.scale.set(0.78, 1.05, 0.2);
  throat.position.set(0, 0.39, -0.83);
  guide.add(...nostrils, throat);

  for (let index = 0; index < 6; index += 1) {
    const crest = mesh(
      new THREE.ConeGeometry(0.1 + index * 0.012, 0.28, 5),
      index % 2 === 0 ? turquoise : lime,
    );
    crest.rotation.x = -0.12;
    crest.position.set(0, 1.06 - index * 0.045, -0.42 + index * 0.19);
    guide.add(crest);
  }

  for (let index = 0; index < 5; index += 1) {
    const spot = mesh(
      new THREE.SphereGeometry(0.13 - index * 0.008, 8, 6),
      index % 2 === 0 ? turquoise : 0xf4c84a,
    );
    spot.scale.set(1, 0.45, 0.22);
    spot.position.set(-0.56, 0.72, -0.22 + index * 0.2);
    guide.add(spot);
  }

  const tailCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 0.63, 0.48),
    new THREE.Vector3(0.16, 0.7, 0.93),
    new THREE.Vector3(0.48, 0.76, 1.25),
    new THREE.Vector3(0.8, 0.74, 1.15),
    new THREE.Vector3(0.86, 0.72, 0.84),
    new THREE.Vector3(0.65, 0.73, 0.68),
    new THREE.Vector3(0.48, 0.75, 0.78),
  ]);
  const tail = mesh(
    new THREE.TubeGeometry(tailCurve, 38, 0.11, 9, false),
    turquoise,
    0.62,
  );

  for (const side of [-1, 1]) {
    for (const z of [-0.35, 0.32]) {
      const leg = mesh(new THREE.CapsuleGeometry(0.065, 0.34, 4, 7), green);
      leg.rotation.z = side * 0.95;
      leg.rotation.x = z > 0 ? 0.4 : -0.4;
      leg.position.set(side * 0.53, 0.3, z);
      const foot = mesh(new THREE.SphereGeometry(0.105, 8, 6), lime);
      foot.scale.set(1.4, 0.45, 0.65);
      foot.position.set(side * 0.72, 0.13, z + (z > 0 ? 0.08 : -0.08));
      guide.add(leg, foot);
    }
  }

  const scarfMaterial = new THREE.MeshStandardMaterial({
    color: 0x397d57,
    roughness: 0.62,
  });
  const scarf = new THREE.Mesh(
    new THREE.TorusGeometry(0.39, 0.075, 7, 18),
    scarfMaterial,
  );
  scarf.rotation.x = Math.PI / 2;
  scarf.position.set(0, 0.72, -0.46);
  guide.userData.scarfMaterial = scarfMaterial;
  const charm = mesh(new THREE.OctahedronGeometry(0.095), 0xffd95a, 0.35);
  charm.name = "chameleon-spark-charm";
  charm.position.set(0, 0.45, -0.83);

  guide.add(
    body,
    belly,
    head,
    snout,
    smile,
    tail,
    scarf,
    charm,
    createBlobShadow(1.15, 0.62, -0.72),
  );
  guide.rotation.y = -0.42;
  return guide;
}

function createKnowledgeSpark(): THREE.Group {
  const spark = new THREE.Group();
  const coreMaterial = new THREE.MeshStandardMaterial({
    color: 0xffd95a,
    emissive: 0xf29b38,
    emissiveIntensity: 1.4,
    roughness: 0.35,
  });
  const core = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.26, 0),
    coreMaterial,
  );
  const ring = mesh(new THREE.TorusGeometry(0.4, 0.035, 6, 18), 0xfff1a3, 0.35);
  ring.rotation.x = Math.PI / 2;
  spark.add(core, ring);
  return spark;
}

function createSanctuary(scene: THREE.Scene): void {
  const waterMaterial = new THREE.MeshStandardMaterial({
    color: 0x3cb6cf,
    metalness: 0.08,
    roughness: 0.28,
  });
  const water = new THREE.Mesh(
    new THREE.CylinderGeometry(35, 35, 0.22, 64),
    waterMaterial,
  );
  water.position.y = -0.82;
  scene.add(water);

  const island = mesh(new THREE.CylinderGeometry(18, 19.2, 1.15, 64), 0x65b95c);
  island.position.y = -0.55;
  scene.add(island);
  const rim = mesh(new THREE.TorusGeometry(18.05, 0.22, 7, 64), 0x9bd36a);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.03;
  scene.add(rim);

  for (const [x, z, scale, color] of [
    [-9, -7, 4.8, 0x55aa5d],
    [9, 7, 4.5, 0x75c95b],
    [-8, 9, 3.9, 0x4ea66a],
    [9, -8, 4.2, 0x80c45e],
  ] as Array<[number, number, number, number]>) {
    const patch = mesh(new THREE.CircleGeometry(scale, 18), color);
    patch.rotation.x = -Math.PI / 2;
    patch.position.set(x, 0.018, z);
    scene.add(patch);
  }

  const plaza = mesh(new THREE.RingGeometry(2.55, 3.75, 48), 0xf2d37d);
  plaza.rotation.x = -Math.PI / 2;
  plaza.position.y = 0.045;
  scene.add(plaza);
  const plazaCentre = mesh(new THREE.CircleGeometry(2.56, 40), 0x72c76b);
  plazaCentre.rotation.x = -Math.PI / 2;
  plazaCentre.position.y = 0.046;
  scene.add(plazaCentre);

  for (const [x, z, length, rotation] of [
    [0, -7.8, 8.2, 0],
    [0, 8.4, 9.4, 0],
    [-8.1, 0, 8.9, Math.PI / 2],
    [8.1, 0, 8.9, Math.PI / 2],
  ] as Array<[number, number, number, number]>) {
    const path = mesh(new THREE.BoxGeometry(1.65, 0.055, length), 0xe8c26d);
    path.position.set(x, 0.032, z);
    path.rotation.y = rotation;
    scene.add(path);
  }

  const meetingStone = mesh(
    new THREE.CylinderGeometry(1.35, 1.55, 0.38, 12),
    0x8e9d91,
  );
  meetingStone.position.set(GUIDE_POSITION.x, 0.19, GUIDE_POSITION.z);
  const moss = mesh(new THREE.CircleGeometry(1.12, 14), 0x8dbe68);
  moss.rotation.x = -Math.PI / 2;
  moss.position.set(GUIDE_POSITION.x, 0.392, GUIDE_POSITION.z);
  scene.add(meetingStone, moss);

  for (let index = 0; index < 40; index += 1) {
    const angle = (index / 40) * Math.PI * 2;
    const stone = mesh(
      new THREE.DodecahedronGeometry(0.3 + (index % 4) * 0.045),
      index % 3 === 0 ? 0xe1d9b9 : 0xaec5aa,
    );
    stone.scale.y = 0.58;
    stone.position.set(Math.sin(angle) * 17.2, 0.2, Math.cos(angle) * 17.2);
    stone.rotation.y = angle;
    scene.add(stone);
  }

  const treePositions: Array<[number, number, number, number]> = [
    [-10, -5, 1.1, 0x2c8b58],
    [-12.5, 4, 0.95, 0x3a9b5e],
    [-7, 10.5, 1.15, 0x27845b],
    [7.5, 10, 1.05, 0x3d9850],
    [12, 3.8, 1.2, 0x267c55],
    [10, -8, 1.08, 0x338f57],
    [-5, -11.5, 1.0, 0x3c985c],
    [5.5, -12, 0.86, 0x398d52],
    [-13, -5.5, 0.82, 0x317f58],
  ];
  for (const [index, [x, z, scale, color]] of treePositions.entries()) {
    const tree = createTree(x, z, scale, color);
    tree.name = `sanctuary-procedural-tree-${index}`;
    scene.add(tree);
  }

  const flowerColors = [0xff587a, 0xffd84f, 0x8b6bd9, 0xffffff, 0x42b7d6];
  for (let index = 0; index < 46; index += 1) {
    const angle = index * 2.399;
    const radius = 4.6 + (index % 9) * 1.3;
    const flower = createFlower(
      flowerColors[index % flowerColors.length] ?? 0xffffff,
    );
    flower.position.set(Math.sin(angle) * radius, 0, Math.cos(angle) * radius);
    flower.rotation.y = angle;
    scene.add(flower);
  }

  for (const [x, z, color] of [
    [-5, 4.5, 0x3e9853],
    [5.2, 6.3, 0x4ca65b],
    [-7.4, -3, 0x388c59],
    [7.3, -4.2, 0x489653],
    [-2.5, -9, 0x3c8b58],
    [12.2, -1.5, 0x3f9257],
  ] as Array<[number, number, number]>) {
    scene.add(createBush(x, z, color));
  }

  scene.add(
    createPortal(0, -13.1, 0, 0x7764bf, 0x9bdcf0, "bone"),
    createPortal(13.1, 0, -Math.PI / 2, 0xe35e7d, 0xffb46d, "muscle"),
    createPortal(-13.1, 0, Math.PI / 2, 0x298d84, 0x7de0c3, "shell"),
    createPortal(0, 13.1, Math.PI, 0x5087c2, 0xb5dcff, "leaf"),
  );

  for (const [x, z] of [
    [-4.1, -3.9],
    [4.2, -4],
    [-4.2, 4.1],
    [4.1, 4.2],
  ] as Array<[number, number]>) {
    scene.add(createLantern(x, z));
  }

  for (const [x, y, z, scale] of [
    [-18, 10, -27, 1.4],
    [14, 12, -30, 1.15],
    [25, 9, -12, 0.9],
  ] as Array<[number, number, number, number]>) {
    const cloud = createCloud();
    cloud.position.set(x, y, z);
    cloud.scale.setScalar(scale);
    scene.add(cloud);
  }

  for (const [x, z, color] of [
    [-1.5, -5.2, 0xffd64d],
    [5.6, 2.2, 0xee6a9d],
    [-6, 1.5, 0x75d8f2],
  ] as Array<[number, number, number]>) {
    const butterfly = createButterfly(color);
    butterfly.position.set(x, 1.7, z);
    scene.add(butterfly);
  }

  const fossilGarden = new THREE.Group();
  fossilGarden.name = "sanctuary-fossil-garden";
  const gardenStone = mesh(
    new THREE.CylinderGeometry(1.15, 1.3, 0.18, 18),
    0xd8c8a4,
  );
  gardenStone.position.set(-3.2, 0.09, -1.7);
  fossilGarden.add(gardenStone);
  for (let index = 0; index < 5; index += 1) {
    const fossil = mesh(
      new THREE.CapsuleGeometry(0.055, 0.42 + index * 0.05, 4, 7),
      0xf7efd8,
    );
    fossil.rotation.z = Math.PI / 2 + (index - 2) * 0.12;
    fossil.position.set(-3.55 + index * 0.18, 0.23, -1.7 + index * 0.08);
    fossilGarden.add(fossil);
  }
  scene.add(fossilGarden);

  const movementGarden = new THREE.Group();
  movementGarden.name = "sanctuary-movement-garden";
  const gardenBase = mesh(
    new THREE.CylinderGeometry(1.15, 1.3, 0.16, 18),
    0xa8c98b,
  );
  gardenBase.position.set(3.1, 0.08, 2.1);
  movementGarden.add(gardenBase);
  for (let index = 0; index < 3; index += 1) {
    const post = mesh(
      new THREE.CylinderGeometry(0.035, 0.05, 1.1, 7),
      0x5f694e,
    );
    post.position.set(2.55 + index * 0.55, 0.58, 2.1);
    const wheel = mesh(new THREE.TorusGeometry(0.25, 0.045, 6, 14), 0xe86d72);
    wheel.position.set(2.55 + index * 0.55, 1.12, 2.1);
    movementGarden.add(post, wheel);
  }
  scene.add(movementGarden);

  const observatory = new THREE.Group();
  observatory.name = "sanctuary-tidepool-observatory";
  const pool = mesh(new THREE.CylinderGeometry(1.25, 1.4, 0.16, 24), 0x55b9c1);
  pool.position.set(-3.1, 0.08, 2.2);
  const shell = mesh(
    new THREE.TorusGeometry(0.34, 0.09, 7, 18, Math.PI * 1.55),
    0xffe2a8,
  );
  shell.rotation.x = Math.PI / 2;
  shell.position.set(-3.1, 0.25, 2.2);
  const sign = mesh(new THREE.BoxGeometry(0.9, 0.55, 0.08), 0x3f7e80);
  sign.position.set(-2.15, 0.72, 2.2);
  const post = mesh(new THREE.CylinderGeometry(0.055, 0.07, 0.85, 7), 0x6b5439);
  post.position.set(-2.15, 0.35, 2.2);
  observatory.add(pool, shell, sign, post);
  scene.add(observatory);

  const careGreenhouse = new THREE.Group();
  careGreenhouse.name = "sanctuary-care-greenhouse";
  const greenhouseBase = mesh(
    new THREE.CylinderGeometry(1.15, 1.25, 0.16, 18),
    0x86aa72,
  );
  greenhouseBase.position.set(0, 0.08, 3.1);
  const glassMaterial = new THREE.MeshStandardMaterial({
    color: 0xb9ebe1,
    transparent: true,
    opacity: 0.62,
    roughness: 0.2,
  });
  const glass = new THREE.Mesh(
    new THREE.CylinderGeometry(0.8, 1, 1.15, 8),
    glassMaterial,
  );
  glass.position.set(0, 0.72, 3.1);
  const roof = mesh(new THREE.ConeGeometry(0.9, 0.55, 8), 0xeab979);
  roof.position.set(0, 1.57, 3.1);
  careGreenhouse.add(greenhouseBase, glass, roof);
  for (const x of [-0.42, 0, 0.42]) {
    const flower = createFlower(x === 0 ? 0xffd966 : 0xe87d8e);
    flower.position.set(x, 0.17, 2.95);
    careGreenhouse.add(flower);
  }
  scene.add(careGreenhouse);

  const celebration = new THREE.Group();
  celebration.name = "sanctuary-celebration";
  const bannerMaterial = new THREE.MeshStandardMaterial({
    color: 0x6b55a7,
    emissive: 0x39286d,
    emissiveIntensity: 0.35,
    roughness: 0.65,
  });
  const banner = new THREE.Mesh(
    new THREE.BoxGeometry(6.8, 0.7, 0.12),
    bannerMaterial,
  );
  banner.position.set(0, 4.25, -3.5);
  celebration.add(banner);
  for (const x of [-3.5, 3.5]) {
    const pole = mesh(new THREE.CylinderGeometry(0.06, 0.08, 4.5, 7), 0x6a5138);
    pole.position.set(x, 2.2, -3.5);
    celebration.add(pole);
  }
  const colors = [0xf1c95b, 0xe87979, 0x6f9ed0, 0x7fc875];
  for (let index = 0; index < 12; index += 1) {
    const lanternMaterial = new THREE.MeshStandardMaterial({
      color: colors[index % colors.length],
      emissive: colors[index % colors.length],
      emissiveIntensity: 0.38,
      roughness: 0.5,
    });
    const lantern = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 9, 7),
      lanternMaterial,
    );
    const angle = (index / 12) * Math.PI * 2;
    lantern.position.set(
      Math.sin(angle) * 4.6,
      2.5 + (index % 3) * 0.45,
      Math.cos(angle) * 4.6,
    );
    celebration.add(lantern);
  }
  for (let index = 0; index < 8; index += 1) {
    const spark = createKnowledgeSpark();
    spark.scale.setScalar(0.45);
    const angle = (index / 8) * Math.PI * 2;
    spark.position.set(Math.sin(angle) * 3, 1.2, Math.cos(angle) * 3);
    celebration.add(spark);
  }
  scene.add(celebration);
}

function createSkeletonSafari(scene: THREE.Scene, centreZ: number): void {
  const safari = new THREE.Group();
  safari.name = "skeleton-safari-world";
  safari.position.z = centreZ;

  const earth = mesh(new THREE.CylinderGeometry(18, 18.7, 0.9, 64), 0x526f45);
  earth.position.y = -0.45;
  const groundMaterial = new THREE.MeshStandardMaterial({
    color: 0x76985b,
    roughness: 0.96,
    map: createGroundTexture(),
  });
  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(18, 64),
    groundMaterial,
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0.015;
  safari.add(earth, ground);

  const trailMaterial = new THREE.MeshStandardMaterial({
    color: 0xa8865b,
    roughness: 1,
  });
  const trail = new THREE.Mesh(
    new THREE.RingGeometry(5.2, 6.15, 64),
    trailMaterial,
  );
  trail.rotation.x = -Math.PI / 2;
  trail.position.y = 0.035;
  trail.scale.z = 1.25;
  safari.add(trail);

  const grassGeometry = new THREE.PlaneGeometry(0.08, 0.42);
  grassGeometry.translate(0, 0.21, 0);
  const grassMaterial = new THREE.MeshStandardMaterial({
    color: 0x476f3d,
    roughness: 1,
    side: THREE.DoubleSide,
  });
  const grass = new THREE.InstancedMesh(grassGeometry, grassMaterial, 520);
  const transform = new THREE.Object3D();
  for (let index = 0; index < 520; index += 1) {
    const angle = seeded(index * 11.31) * Math.PI * 2;
    const radius = 2.2 + seeded(index * 7.17 + 3) * 15.1;
    transform.position.set(
      Math.sin(angle) * radius,
      0.02,
      Math.cos(angle) * radius,
    );
    transform.rotation.set(0, seeded(index * 2.9) * Math.PI, 0);
    const scale = 0.65 + seeded(index * 4.71) * 0.8;
    transform.scale.set(scale, scale, scale);
    transform.updateMatrix();
    grass.setMatrixAt(index, transform.matrix);
  }
  grass.instanceMatrix.needsUpdate = true;
  safari.add(grass);

  const forestTrees: Array<[number, number, number]> = [
    [-13, -8, 1.2],
    [-15, -1, 1.05],
    [-13, 7, 1.25],
    [-8, 13, 1.05],
    [7, 14, 1.15],
    [13, 9, 1.3],
    [15, 2, 1.1],
    [13, -7, 1.2],
    [8, -14, 1.18],
    [-7, -14, 1.08],
  ];
  for (const [index, [x, z, scale]] of forestTrees.entries()) {
    const tree = createForestTree(scale);
    tree.name = `safari-procedural-tree-${index}`;
    tree.position.set(x, 0, z);
    safari.add(tree);
  }

  for (let index = 0; index < 22; index += 1) {
    const angle = seeded(index * 8.2) * Math.PI * 2;
    const radius = 7 + seeded(index * 2.4 + 1) * 9;
    const rock = mesh(
      new THREE.DodecahedronGeometry(0.25 + seeded(index) * 0.4, 1),
      index % 2 === 0 ? 0x797e70 : 0x9b9987,
    );
    rock.scale.y = 0.55;
    rock.position.set(Math.sin(angle) * radius, 0.2, Math.cos(angle) * radius);
    safari.add(rock);
  }

  const bunny = createBunny();
  bunny.position.set(0, REGIONAL_GUIDE_GROUND_Y, 5);
  bunny.rotation.y = 0.25;
  safari.add(bunny);

  safari.add(
    createSafariStation("fossil", -8, 0),
    createSafariStation("guardian", 7.5, -1),
    createSafariStation("bridge", -7.5, -9),
    createSafariStation("model", 7.5, -10),
    createSafariStation("lab", 0, -14),
  );

  const returnPortal = createPortal(0, 13, Math.PI, 0x736859, 0xc5e5d8, "bone");
  returnPortal.scale.setScalar(0.82);
  safari.add(returnPortal);

  const restoration = new THREE.Group();
  restoration.name = "safari-restoration";
  const beamMaterial = new THREE.MeshBasicMaterial({
    color: 0xffef9c,
    transparent: true,
    opacity: 0.22,
    depthWrite: false,
  });
  const beam = new THREE.Mesh(
    new THREE.CylinderGeometry(1.8, 0.45, 8, 24),
    beamMaterial,
  );
  beam.position.set(0, 4, -5.5);
  restoration.add(beam);
  for (let index = 0; index < 18; index += 1) {
    const flower = createFlower(index % 2 === 0 ? 0xf3d46b : 0xf4f0df);
    const angle = (index / 18) * Math.PI * 2;
    flower.position.set(Math.sin(angle) * 3.2, 0, -5.5 + Math.cos(angle) * 3.2);
    flower.scale.setScalar(1.3);
    restoration.add(flower);
  }
  safari.add(restoration);
  scene.add(safari);
}

function createMuscleMeadow(scene: THREE.Scene, centreX: number): void {
  const meadow = new THREE.Group();
  meadow.name = "muscle-meadow-world";
  meadow.position.x = centreX;

  const earth = mesh(new THREE.CylinderGeometry(18, 18.8, 0.9, 64), 0x4d744b);
  earth.position.y = -0.45;
  const groundMaterial = new THREE.MeshStandardMaterial({
    color: 0x7eaa63,
    roughness: 0.96,
    map: createGroundTexture(),
  });
  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(18, 64),
    groundMaterial,
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0.015;
  meadow.add(earth, ground);

  const streamMaterial = new THREE.MeshStandardMaterial({
    color: 0x397f8d,
    roughness: 0.35,
    metalness: 0.05,
  });
  for (let index = -5; index <= 5; index += 1) {
    const pool = new THREE.Mesh(
      new THREE.CircleGeometry(1.55 + (index % 2) * 0.18, 20),
      streamMaterial,
    );
    pool.rotation.x = -Math.PI / 2;
    pool.position.set(Math.sin(index * 0.8) * 1.1, 0.04, index * 2.45);
    meadow.add(pool);
  }

  const treePositions: Array<[number, number, number]> = [
    [-12, -10, 0.95],
    [-15, -3, 1.08],
    [-13, 8, 1.0],
    [-7, 14, 1.1],
    [7, 14, 0.96],
    [14, 8, 1.12],
    [15, -6, 1.0],
    [9, -14, 1.08],
  ];
  for (const [x, z, scale] of treePositions) {
    const tree = createForestTree(scale);
    tree.position.set(x, 0, z);
    meadow.add(tree);
  }

  for (let index = 0; index < 54; index += 1) {
    const angle = seeded(index * 5.91) * Math.PI * 2;
    const radius = 4 + seeded(index * 3.17 + 4) * 12.5;
    const flower = createFlower(
      [0xf4d35e, 0xe97883, 0x8f7ac8, 0xf6eee0][index % 4] ?? 0xffffff,
    );
    flower.position.set(Math.sin(angle) * radius, 0, Math.cos(angle) * radius);
    meadow.add(flower);
  }

  const beaver = createBeaver();
  beaver.position.set(-5, REGIONAL_GUIDE_GROUND_Y, 0);
  beaver.rotation.y = -0.7;
  meadow.add(beaver);

  meadow.add(
    createMeadowStation("pair", 0, -7),
    createMeadowStation("book", 7, -2),
    createMeadowStation("elastic", 7, 8),
    createMeadowStation("health", -5, 8),
  );

  const returnPortal = createPortal(
    -13,
    0,
    Math.PI / 2,
    0x9a556b,
    0xffc08d,
    "muscle",
  );
  returnPortal.scale.setScalar(0.82);
  meadow.add(returnPortal);

  const restoration = new THREE.Group();
  restoration.name = "meadow-restoration";
  const brightWaterMaterial = new THREE.MeshStandardMaterial({
    color: 0x66d7dd,
    emissive: 0x236f78,
    emissiveIntensity: 0.35,
    roughness: 0.24,
  });
  for (let index = -5; index <= 5; index += 1) {
    const ripple = new THREE.Mesh(
      new THREE.TorusGeometry(
        0.55 + (Math.abs(index) % 3) * 0.12,
        0.045,
        6,
        18,
      ),
      brightWaterMaterial,
    );
    ripple.rotation.x = Math.PI / 2;
    ripple.position.set(Math.sin(index * 0.8) * 1.1, 0.1, index * 2.45);
    restoration.add(ripple);
  }
  for (let index = 0; index < 10; index += 1) {
    const spark = createKnowledgeSpark();
    spark.scale.setScalar(0.45);
    spark.position.set(
      Math.sin(index) * 1.4,
      0.55 + (index % 3) * 0.25,
      -10 + index * 2.2,
    );
    restoration.add(spark);
  }
  meadow.add(restoration);
  scene.add(meadow);
}

function createClassificationCoast(scene: THREE.Scene, centreX: number): void {
  const coast = new THREE.Group();
  coast.name = "classification-coast-world";
  coast.position.x = centreX;

  const water = mesh(new THREE.CylinderGeometry(24, 24, 0.35, 64), 0x3d9eb5);
  water.position.y = -0.72;
  coast.add(water);
  const island = mesh(
    new THREE.CylinderGeometry(17.8, 18.6, 0.9, 64),
    0xd5bc72,
  );
  island.position.y = -0.42;
  coast.add(island);
  for (const [x, z, radius, color] of [
    [-4, -4, 9, 0xe5cf86],
    [6, 5, 8, 0xb7bd69],
    [-7, 9, 5, 0x86aa61],
  ] as Array<[number, number, number, number]>) {
    const patch = mesh(new THREE.CircleGeometry(radius, 32), color);
    patch.rotation.x = -Math.PI / 2;
    patch.position.set(x, 0.025, z);
    coast.add(patch);
  }

  for (const [x, z, radius] of [
    [-8, -3, 1.7],
    [-5.5, 5, 1.3],
    [5, -7, 1.5],
  ] as Array<[number, number, number]>) {
    const tidepool = mesh(new THREE.CircleGeometry(radius, 22), 0x4eb5c4);
    tidepool.rotation.x = -Math.PI / 2;
    tidepool.position.set(x, 0.055, z);
    coast.add(tidepool);
    for (let index = 0; index < 5; index += 1) {
      const rock = mesh(
        new THREE.DodecahedronGeometry(0.28 + index * 0.025),
        index % 2 ? 0x87958c : 0xa39d83,
      );
      const angle = (index / 5) * Math.PI * 2;
      rock.scale.y = 0.6;
      rock.position.set(
        x + Math.sin(angle) * radius,
        0.18,
        z + Math.cos(angle) * radius,
      );
      coast.add(rock);
    }
  }

  for (const [x, z, scale] of [
    [-13, -9, 0.85],
    [-14, 6, 1],
    [11, 11, 0.9],
    [14, -4, 0.82],
  ] as Array<[number, number, number]>) {
    const tree = createTree(x, z, scale, 0x3e8e5d);
    coast.add(tree);
  }
  for (let index = 0; index < 12; index += 1) {
    const angle = index * 2.17;
    const radius = 5 + (index % 9) * 1.25;
    const grass = createBush(0, 0, index % 2 ? 0x638f58 : 0x829f58);
    grass.scale.setScalar(0.34);
    grass.position.set(Math.sin(angle) * radius, 0, Math.cos(angle) * radius);
    coast.add(grass);
  }

  const crab = createCoveCrab();
  crab.position.set(5, REGIONAL_GUIDE_GROUND_Y, 0);
  crab.rotation.y = 0.6;
  coast.add(crab);

  const animals: Array<[string, number, number, number]> = [
    ["fish", -3, -10, 0],
    ["frog", -8, -1, 0.8],
    ["gecko", 8, 10, -0.4],
    ["bird", -10, 8, 0.5],
    ["seal", 11, -4, -0.8],
    ["crab", -7, -5, 0.2],
    ["octopus", 3, -8, 0],
  ];
  for (const [kind, x, z, rotation] of animals) {
    const animal = createCoastAnimal(kind);
    animal.position.set(x, kind === "bird" ? 1.1 : 0.08, z);
    animal.rotation.y = rotation;
    coast.add(animal);
  }

  coast.add(
    createCoastStation("backbone", 0, -8),
    createCoastStation("armour", -8, -3),
    createCoastStation("groups", -7, 8),
    createCoastStation("key", 3, 10),
    createCoastStation("repair", 11, 7),
  );
  const portal = createPortal(13, 0, -Math.PI / 2, 0x298d84, 0x8de4dc, "shell");
  portal.scale.setScalar(0.82);
  coast.add(portal);

  const lighthouse = new THREE.Group();
  const tower = mesh(new THREE.CylinderGeometry(0.55, 0.8, 4.2, 16), 0xf1ead8);
  tower.position.set(7.5, 2.1, 13);
  const stripe = mesh(
    new THREE.CylinderGeometry(0.64, 0.66, 0.7, 16),
    0x4a8b94,
  );
  stripe.position.set(7.5, 2.4, 13);
  const roof = mesh(new THREE.ConeGeometry(0.85, 0.65, 16), 0xdd6a55);
  roof.position.set(7.5, 4.8, 13);
  lighthouse.add(tower, stripe, roof);
  coast.add(lighthouse);

  const restoration = new THREE.Group();
  restoration.name = "coast-restoration";
  const beamMaterial = new THREE.MeshBasicMaterial({
    color: 0xffed9b,
    transparent: true,
    opacity: 0.3,
    depthWrite: false,
  });
  const beam = new THREE.Mesh(
    new THREE.ConeGeometry(2.8, 10, 18, 1, true),
    beamMaterial,
  );
  beam.rotation.z = -Math.PI / 2;
  beam.position.set(2.7, 4.3, 13);
  restoration.add(beam);
  for (const [x, z] of [
    [0, -8],
    [-8, -3],
    [-7, 8],
    [3, 10],
    [11, 7],
  ] as Array<[number, number]>) {
    const glow = createKnowledgeSpark();
    glow.position.set(x, 2.8, z);
    glow.scale.setScalar(0.75);
    restoration.add(glow);
  }
  coast.add(restoration);
  scene.add(coast);
}

function createCoveCrab(): THREE.Group {
  const crab = new THREE.Group();
  crab.name = "cove-crab-character";
  const coral = 0xd8684d;
  const body = mesh(new THREE.SphereGeometry(0.58, 14, 10), coral, 0.82);
  body.scale.set(1.2, 0.72, 0.88);
  body.position.y = 0.55;
  crab.add(body, createBlobShadow(0.82, 0.58, 0.02));
  for (const side of [-1, 1]) {
    const stalk = mesh(new THREE.CapsuleGeometry(0.04, 0.2, 4, 7), coral);
    stalk.position.set(side * 0.24, 0.96, -0.25);
    const eye = mesh(new THREE.SphereGeometry(0.11, 10, 8), 0xfffbec);
    eye.position.set(side * 0.24, 1.08, -0.3);
    const pupil = mesh(new THREE.SphereGeometry(0.052, 8, 6), 0x263338);
    pupil.position.set(side * 0.24, 1.08, -0.39);
    const arm = mesh(new THREE.CapsuleGeometry(0.1, 0.62, 4, 8), coral);
    arm.rotation.z = side * 0.9;
    arm.position.set(side * 0.7, 0.55, -0.02);
    const claw = mesh(new THREE.TorusGeometry(0.19, 0.09, 6, 10, 4.4), coral);
    claw.rotation.set(Math.PI / 2, 0, side * 0.65);
    claw.position.set(side * 1.02, 0.76, -0.12);
    crab.add(stalk, eye, pupil, arm, claw);
    for (let index = 0; index < 3; index += 1) {
      const leg = mesh(new THREE.CapsuleGeometry(0.045, 0.38, 3, 6), coral);
      leg.rotation.z = side * (0.72 + index * 0.08);
      leg.position.set(side * (0.38 + index * 0.12), 0.22, 0.16 + index * 0.14);
      crab.add(leg);
    }
  }
  const smile = mesh(
    new THREE.TorusGeometry(0.12, 0.018, 5, 12, Math.PI),
    0x63372f,
  );
  smile.rotation.z = Math.PI;
  smile.position.set(0, 0.55, -0.56);
  const badge = mesh(new THREE.OctahedronGeometry(0.12), 0xf1c95b);
  badge.name = "cove-pathfinder-badge";
  badge.position.set(0.38, 0.54, -0.58);
  crab.add(smile, badge);
  return crab;
}

function createCoastStation(kind: string, x: number, z: number): THREE.Group {
  const station = new THREE.Group();
  station.position.set(x, 0, z);
  const base = mesh(new THREE.CylinderGeometry(0.9, 1.05, 0.22, 12), 0x8a8f78);
  base.position.y = 0.11;
  const post = mesh(new THREE.CylinderGeometry(0.08, 0.11, 1.65, 8), 0x66533e);
  post.position.y = 0.95;
  const sign = mesh(
    new THREE.BoxGeometry(1.8, 0.9, 0.16),
    kind === "key" || kind === "repair" ? 0x477f8e : 0x5c8769,
  );
  sign.position.y = 1.65;
  const beacon = mesh(new THREE.OctahedronGeometry(0.22), 0x9bb8b6);
  beacon.position.y = 2.45;
  station.add(base, post, sign, beacon);
  const marks = kind === "groups" ? 5 : kind === "key" ? 3 : 2;
  for (let index = 0; index < marks; index += 1) {
    const mark = mesh(new THREE.CircleGeometry(0.11, 8), 0xf4e8b0);
    mark.position.set(
      -0.55 + index * (1.1 / Math.max(1, marks - 1)),
      1.65,
      -0.09,
    );
    mark.rotation.y = Math.PI;
    station.add(mark);
  }
  return station;
}

function createCoastAnimal(kind: string): THREE.Group {
  const animal = new THREE.Group();
  animal.name = `coast-${kind}-character`;
  const colors: Record<string, number> = {
    fish: 0x4f89a1,
    frog: 0x6d9c58,
    gecko: 0x8b9b59,
    bird: 0x283b4c,
    seal: 0x768489,
    crab: 0xc96b4d,
    octopus: 0x9b668f,
  };
  const color = colors[kind] ?? 0x6b8b7d;
  if (kind === "bird") {
    const body = mesh(new THREE.SphereGeometry(0.3, 10, 8), color);
    body.scale.set(0.75, 1.25, 0.8);
    const chest = mesh(new THREE.SphereGeometry(0.22, 9, 7), 0xf2eee0);
    chest.position.set(0, -0.02, -0.18);
    const beak = mesh(new THREE.ConeGeometry(0.1, 0.28, 6), 0xe8844e);
    beak.rotation.x = Math.PI / 2;
    beak.position.set(0, 0.14, -0.38);
    animal.add(body, chest, beak);
  } else if (kind === "crab") {
    const body = mesh(new THREE.SphereGeometry(0.32, 10, 7), color);
    body.scale.set(1.25, 0.55, 0.8);
    animal.add(body);
    for (const side of [-1, 1]) {
      for (let index = 0; index < 3; index += 1) {
        const leg = mesh(new THREE.CapsuleGeometry(0.035, 0.34, 3, 6), color);
        leg.rotation.z = side * 0.85;
        leg.position.set(side * (0.28 + index * 0.09), 0, -0.1 + index * 0.13);
        animal.add(leg);
      }
    }
  } else if (kind === "octopus") {
    const head = mesh(new THREE.SphereGeometry(0.32, 12, 9), color);
    head.scale.y = 1.2;
    head.position.y = 0.22;
    animal.add(head);
    for (let index = 0; index < 6; index += 1) {
      const arm = mesh(new THREE.CapsuleGeometry(0.045, 0.4, 3, 6), color);
      arm.rotation.z = (index - 2.5) * 0.3;
      arm.position.set((index - 2.5) * 0.12, -0.08, 0);
      animal.add(arm);
    }
  } else {
    const body = mesh(new THREE.SphereGeometry(0.3, 12, 8), color);
    body.scale.set(kind === "fish" || kind === "seal" ? 1.5 : 1.15, 0.65, 0.78);
    const head = mesh(new THREE.SphereGeometry(0.2, 10, 7), color);
    head.position.set(0, 0.08, -0.4);
    animal.add(body, head);
    if (kind === "fish") {
      const tail = mesh(new THREE.ConeGeometry(0.22, 0.4, 3), color);
      tail.rotation.x = Math.PI / 2;
      tail.position.set(0, 0, 0.58);
      animal.add(tail);
    }
  }
  const eyeY = kind === "bird" ? 0.16 : kind === "octopus" ? 0.3 : 0.12;
  const eyeZ = kind === "crab" ? -0.27 : kind === "bird" ? -0.28 : -0.56;
  const eyeX = kind === "crab" ? 0.18 : kind === "octopus" ? 0.13 : 0.1;
  for (const side of [-1, 1]) {
    if (kind === "crab") {
      const stalk = mesh(new THREE.CapsuleGeometry(0.025, 0.16, 3, 5), color);
      stalk.position.set(side * eyeX, 0.18, eyeZ + 0.08);
      animal.add(stalk);
    }
    const white = mesh(new THREE.SphereGeometry(0.052, 8, 6), 0xfffbec);
    white.position.set(side * eyeX, eyeY, eyeZ);
    const pupil = mesh(new THREE.SphereGeometry(0.026, 7, 5), 0x192627);
    pupil.position.set(side * eyeX, eyeY, eyeZ - 0.04);
    const glint = mesh(new THREE.SphereGeometry(0.009, 5, 4), 0xffffff);
    glint.position.set(side * eyeX - 0.007, eyeY + 0.012, eyeZ - 0.06);
    animal.add(white, pupil, glint);
  }
  animal.scale.setScalar(1.15);
  animal.add(createBlobShadow(0.45, 0.35, -0.22));
  return animal;
}

function createHealingHabitat(scene: THREE.Scene, centreZ: number): void {
  const habitat = new THREE.Group();
  habitat.name = "healing-habitat-world";
  habitat.position.z = centreZ;

  const earth = mesh(new THREE.CylinderGeometry(18, 18.8, 0.9, 64), 0x527451);
  earth.position.y = -0.45;
  const groundMaterial = new THREE.MeshStandardMaterial({
    color: 0x86ad70,
    roughness: 0.96,
    map: createGroundTexture(),
  });
  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(18, 64),
    groundMaterial,
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0.015;
  habitat.add(earth, ground);

  const pathMaterial = new THREE.MeshStandardMaterial({
    color: 0xe6d49b,
    roughness: 0.92,
  });
  for (const [x, z, length, rotation] of [
    [0, 2, 24, 0],
    [-4, 0, 15, Math.PI / 2],
    [4, 9, 15, Math.PI / 2],
  ] as Array<[number, number, number, number]>) {
    const path = new THREE.Mesh(
      new THREE.BoxGeometry(1.45, 0.07, length),
      pathMaterial,
    );
    path.position.set(x, 0.045, z);
    path.rotation.y = rotation;
    habitat.add(path);
  }

  for (const [x, z, scale] of [
    [-13, -9, 0.92],
    [-15, 2, 1],
    [-12, 12, 1.05],
    [12, 12, 0.94],
    [15, 2, 1.08],
    [12, -10, 0.9],
  ] as Array<[number, number, number]>) {
    const tree = createForestTree(scale);
    tree.position.set(x, 0, z);
    habitat.add(tree);
  }
  for (let index = 0; index < 28; index += 1) {
    const angle = index * 2.31;
    const radius = 4.3 + (index % 7) * 1.65;
    const flower = createFlower(
      [0xf5cf67, 0xee8a91, 0x8c79c6, 0xf4eee0][index % 4] ?? 0xffffff,
    );
    flower.position.set(Math.sin(angle) * radius, 0, Math.cos(angle) * radius);
    habitat.add(flower);
  }

  const panda = createPanda();
  panda.position.set(0, REGIONAL_GUIDE_GROUND_Y, -5);
  panda.rotation.y = Math.PI;
  habitat.add(panda);

  habitat.add(
    createHealthStation("care", -8, 0),
    createHealthStation("forms", 7.5, 1),
    createHealthStation("germs", -7.5, 9),
    createHealthStation("prevention", 7.5, 10),
    createHealthStation("research", 0, 14),
  );
  const returnPortal = createPortal(
    0,
    -13,
    Math.PI,
    0x5087c2,
    0xb5dcff,
    "leaf",
  );
  returnPortal.scale.setScalar(0.82);
  habitat.add(returnPortal);

  const greenhouse = new THREE.Group();
  const frameMaterial = new THREE.MeshStandardMaterial({
    color: 0x477462,
    roughness: 0.7,
  });
  for (const x of [-2.1, 2.1]) {
    const frame = new THREE.Mesh(
      new THREE.BoxGeometry(0.13, 3.4, 5),
      frameMaterial,
    );
    frame.position.set(x, 1.7, 14);
    habitat.add(frame);
  }
  const glassMaterial = new THREE.MeshStandardMaterial({
    color: 0xb7e2d4,
    transparent: true,
    opacity: 0.35,
    roughness: 0.18,
  });
  const glass = new THREE.Mesh(
    new THREE.BoxGeometry(4.3, 3.2, 5.2),
    glassMaterial,
  );
  glass.position.set(0, 1.65, 14);
  const roof = new THREE.Mesh(
    new THREE.ConeGeometry(3.35, 1.7, 4),
    glassMaterial,
  );
  roof.rotation.y = Math.PI / 4;
  roof.scale.z = 0.78;
  roof.position.set(0, 4.05, 14);
  greenhouse.add(glass, roof);
  habitat.add(greenhouse);

  const restoration = new THREE.Group();
  restoration.name = "health-restoration";
  for (const [x, z] of [
    [-8, 0],
    [7.5, 1],
    [-7.5, 9],
    [7.5, 10],
    [0, 14],
  ] as Array<[number, number]>) {
    const lantern = createKnowledgeSpark();
    lantern.scale.setScalar(0.72);
    lantern.position.set(x, 2.8, z);
    restoration.add(lantern);
  }
  const warmGlass = new THREE.MeshStandardMaterial({
    color: 0x9ee8bd,
    emissive: 0x3a805d,
    emissiveIntensity: 0.5,
    transparent: true,
    opacity: 0.24,
    side: THREE.DoubleSide,
  });
  const glow = new THREE.Mesh(new THREE.BoxGeometry(4.1, 3, 5), warmGlass);
  glow.position.set(0, 1.65, 14);
  restoration.add(glow);
  for (let index = 0; index < 12; index += 1) {
    const sparkle = createKnowledgeSpark();
    sparkle.scale.setScalar(0.3);
    sparkle.position.set(
      -1.7 + (index % 4) * 1.1,
      0.55 + Math.floor(index / 4) * 0.7,
      12.2 + (index % 3) * 1.6,
    );
    restoration.add(sparkle);
  }
  habitat.add(restoration);
  scene.add(habitat);
}

function createPanda(): THREE.Group {
  const panda = new THREE.Group();
  panda.name = "sage-panda-character";
  const white = 0xf3eee2;
  const dark = 0x303335;
  const cream = 0xfff8e8;
  const body = mesh(new THREE.CapsuleGeometry(0.42, 0.72, 6, 12), white, 0.9);
  body.position.y = 0.8;
  const head = mesh(new THREE.SphereGeometry(0.44, 16, 12), white, 0.9);
  head.position.set(0, 1.52, -0.05);
  const muzzle = mesh(new THREE.SphereGeometry(0.27, 12, 9), cream);
  muzzle.scale.set(1.2, 0.68, 0.65);
  muzzle.position.set(0, 1.43, -0.38);
  panda.add(body, head, muzzle, createBlobShadow(0.58, 0.48, 0.02));
  for (const side of [-1, 1]) {
    const ear = mesh(new THREE.ConeGeometry(0.17, 0.28, 8), dark);
    ear.position.set(side * 0.3, 1.87, -0.02);
    const eyePatch = mesh(new THREE.SphereGeometry(0.1, 8, 6), dark);
    eyePatch.scale.set(0.7, 1.2, 0.35);
    eyePatch.position.set(side * 0.17, 1.58, -0.4);
    const eye = mesh(new THREE.SphereGeometry(0.052, 9, 7), dark);
    eye.position.set(side * 0.17, 1.59, -0.48);
    const glint = mesh(new THREE.SphereGeometry(0.014, 6, 5), 0xffffff);
    glint.position.set(side * 0.17 - 0.009, 1.61, -0.522);
    const brow = mesh(new THREE.CapsuleGeometry(0.013, 0.1, 3, 6), cream);
    brow.rotation.z = Math.PI / 2 + side * 0.16;
    brow.position.set(side * 0.18, 1.72, -0.43);
    const leg = mesh(new THREE.CapsuleGeometry(0.1, 0.38, 4, 7), dark);
    leg.position.set(side * 0.22, 0.3, -0.05);
    panda.add(ear, eyePatch, eye, glint, brow, leg);
  }
  const nose = mesh(new THREE.SphereGeometry(0.065, 8, 6), dark);
  nose.position.set(0, 1.48, -0.56);
  const tail = mesh(new THREE.SphereGeometry(0.16, 10, 7), dark);
  tail.position.set(0, 0.48, 0.48);
  const smile = mesh(
    new THREE.TorusGeometry(0.09, 0.014, 5, 10, Math.PI),
    dark,
  );
  smile.rotation.z = Math.PI;
  smile.position.set(0, 1.38, -0.57);
  const satchel = mesh(new THREE.CapsuleGeometry(0.18, 0.16, 5, 9), 0x527b69);
  satchel.name = "sage-herbalist-satchel";
  satchel.scale.z = 0.42;
  satchel.position.set(0.34, 0.88, -0.35);
  const satchelFlap = mesh(new THREE.SphereGeometry(0.13, 9, 7), 0x86ad70);
  satchelFlap.scale.set(1, 0.55, 0.25);
  satchelFlap.position.set(0.34, 0.95, -0.43);
  panda.add(nose, smile, tail, satchel, satchelFlap);
  panda.scale.setScalar(1.12);
  return panda;
}

function createHealthStation(kind: string, x: number, z: number): THREE.Group {
  const station = new THREE.Group();
  station.position.set(x, 0, z);
  const base = mesh(new THREE.CylinderGeometry(1, 1.15, 0.18, 14), 0x8fa989);
  base.position.y = 0.09;
  const post = mesh(new THREE.CylinderGeometry(0.07, 0.1, 1.6, 8), 0x66533e);
  post.position.y = 0.9;
  const sign = mesh(
    new THREE.BoxGeometry(2, 1, 0.16),
    kind === "care" ? 0x657e9a : 0x55866f,
  );
  sign.position.y = 1.65;
  const lantern = mesh(new THREE.OctahedronGeometry(0.22), 0xb4c4aa);
  lantern.position.y = 2.65;
  station.add(base, post, sign, lantern);
  if (kind === "forms") {
    for (let index = 0; index < 4; index += 1) {
      const shape = mesh(
        index % 2
          ? new THREE.CapsuleGeometry(0.07, 0.2, 3, 6)
          : new THREE.BoxGeometry(0.18, 0.28, 0.08),
        index % 2 ? 0xe6ba73 : 0x8fb6ba,
      );
      shape.position.set(-0.55 + index * 0.36, 1.65, -0.12);
      station.add(shape);
    }
  } else if (kind === "prevention" || kind === "research") {
    for (let index = 0; index < 3; index += 1) {
      const flower = createFlower(index % 2 ? 0xf3cf6a : 0xe9828e);
      flower.position.set(-0.65 + index * 0.65, 0.2, -0.8);
      station.add(flower);
    }
  } else {
    const emblem = mesh(
      kind === "care"
        ? new THREE.TorusGeometry(0.24, 0.08, 6, 14)
        : new THREE.SphereGeometry(0.17, 8, 6),
      0xf3df9a,
    );
    emblem.position.set(0, 1.65, -0.13);
    station.add(emblem);
  }
  return station;
}

function createBeaver(): THREE.Group {
  const beaver = new THREE.Group();
  beaver.name = "mara-beaver-character";
  const fur = 0x72513e;
  const bellyColor = 0xc6a783;
  const body = mesh(new THREE.CapsuleGeometry(0.48, 0.85, 6, 12), fur, 0.9);
  body.position.y = 0.82;
  const head = mesh(new THREE.SphereGeometry(0.42, 16, 12), fur, 0.9);
  head.position.set(0, 1.55, -0.08);
  const muzzle = mesh(new THREE.SphereGeometry(0.24, 12, 9), bellyColor);
  muzzle.scale.set(1.25, 0.75, 0.72);
  muzzle.position.set(0, 1.45, -0.38);
  const belly = mesh(new THREE.SphereGeometry(0.38, 14, 10), bellyColor);
  belly.scale.set(0.8, 1.35, 0.35);
  belly.position.set(0, 0.83, -0.4);
  const tail = mesh(new THREE.SphereGeometry(0.42, 10, 7), 0x4f382f);
  tail.name = "mara-beaver-paddle-tail";
  tail.scale.set(0.62, 0.16, 1.45);
  tail.rotation.x = 0.35;
  tail.position.set(0, 0.24, 0.82);
  beaver.add(
    body,
    head,
    muzzle,
    belly,
    tail,
    createBlobShadow(0.62, 0.52, 0.02),
  );
  for (const x of [-0.17, 0.17]) {
    const eye = mesh(new THREE.SphereGeometry(0.062, 10, 7), 0x191b18);
    eye.position.set(x, 1.63, -0.39);
    const glint = mesh(new THREE.SphereGeometry(0.016, 6, 5), 0xffffff);
    glint.position.set(x - 0.01, 1.652, -0.438);
    const ear = mesh(new THREE.SphereGeometry(0.1, 9, 7), fur);
    ear.position.set(x * 1.85, 1.78, -0.02);
    const innerEar = mesh(new THREE.SphereGeometry(0.055, 8, 6), 0xd09b8b);
    innerEar.position.set(x * 1.85, 1.79, -0.1);
    beaver.add(eye, glint, ear, innerEar);
  }
  const nose = mesh(new THREE.SphereGeometry(0.075, 8, 6), 0x25201f);
  nose.position.set(0, 1.51, -0.57);
  const teeth = mesh(new THREE.BoxGeometry(0.16, 0.2, 0.06), 0xfff8df);
  teeth.name = "mara-beaver-teeth";
  teeth.position.set(0, 1.37, -0.59);
  const neckerchief = mesh(
    new THREE.TorusGeometry(0.31, 0.045, 6, 16),
    0xe97883,
  );
  neckerchief.name = "mara-motion-neckerchief";
  neckerchief.rotation.x = Math.PI / 2;
  neckerchief.position.set(0, 1.23, -0.05);
  const charm = mesh(new THREE.TorusGeometry(0.075, 0.025, 5, 10), 0xf4d35e);
  charm.position.set(0, 1.1, -0.32);
  beaver.add(
    nose,
    teeth,
    neckerchief,
    charm,
    ...createWhiskers(1.46, -0.57, 0x49362e),
  );
  beaver.scale.setScalar(1.12);
  return beaver;
}

function createMeadowStation(
  kind: "pair" | "book" | "elastic" | "health",
  x: number,
  z: number,
): THREE.Group {
  const station = new THREE.Group();
  station.position.set(x, 0, z);
  const beaconMaterial = new THREE.MeshStandardMaterial({
    color: 0xffb873,
    emissive: 0x9d4d3f,
    emissiveIntensity: 0.5,
  });
  const beacon = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.22),
    beaconMaterial,
  );
  beacon.position.y = 2.7;
  station.add(beacon);

  if (kind === "pair") {
    const arm = createArmMechanismModel();
    arm.scale.setScalar(1.15);
    station.add(arm);
  } else if (kind === "book") {
    const stand = mesh(new THREE.BoxGeometry(2.3, 0.16, 1.2), 0x735039);
    stand.position.y = 0.82;
    const book = mesh(new THREE.BoxGeometry(0.8, 0.2, 0.58), 0x4c6596);
    book.position.set(0.55, 1.05, 0);
    station.add(stand, book, createArmMechanismModel());
  } else if (kind === "elastic") {
    const board = mesh(new THREE.BoxGeometry(2.8, 2.05, 0.14), 0xd5bf8e);
    board.position.y = 1.2;
    const arm = createArmMechanismModel();
    arm.scale.setScalar(0.78);
    arm.position.set(0, 0.25, -0.15);
    station.add(board, arm);
  } else {
    const activityColors = [0xe87870, 0x5c94bd, 0xe0bc4f, 0x6a9f68];
    for (let index = 0; index < 5; index += 1) {
      const step = mesh(
        new THREE.CylinderGeometry(0.48, 0.55, 0.12, 16),
        activityColors[index % activityColors.length] ?? 0xffffff,
      );
      step.position.set(-1.8 + index * 0.9, 0.06, Math.sin(index * 1.7) * 0.55);
      station.add(step);
    }
  }
  return station;
}

function createArmMechanismModel(): THREE.Group {
  const arm = new THREE.Group();
  const boneColor = 0xeee1bd;
  const upper = mesh(new THREE.CapsuleGeometry(0.09, 0.9, 5, 9), boneColor);
  upper.position.set(-0.42, 1.35, 0);
  const lower = mesh(new THREE.CapsuleGeometry(0.08, 0.9, 5, 9), boneColor);
  lower.rotation.z = Math.PI / 2.8;
  lower.position.set(0.05, 0.82, 0);
  const joint = mesh(new THREE.SphereGeometry(0.15, 10, 8), 0xcaa869);
  joint.position.set(-0.4, 0.79, 0);
  const front = mesh(new THREE.CapsuleGeometry(0.12, 0.62, 5, 9), 0xb54d59);
  front.position.set(-0.12, 1.28, -0.14);
  front.rotation.z = -0.35;
  const back = mesh(new THREE.CapsuleGeometry(0.075, 0.82, 5, 9), 0x7e4058);
  back.position.set(-0.63, 1.25, 0.14);
  arm.add(upper, lower, joint, front, back);
  return arm;
}

function createGroundTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const context = canvas.getContext("2d");
  if (!context) return new THREE.CanvasTexture(canvas);
  context.fillStyle = "#78985f";
  context.fillRect(0, 0, 256, 256);
  for (let index = 0; index < 1600; index += 1) {
    const shade = 80 + Math.floor(seeded(index * 3.7) * 55);
    context.fillStyle = `rgba(${shade - 20},${shade + 25},${shade - 30},0.18)`;
    const x = seeded(index * 8.1) * 256;
    const y = seeded(index * 5.3 + 7) * 256;
    const size = 1 + seeded(index * 2.1) * 3;
    context.fillRect(x, y, size, size);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(5, 5);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function seeded(value: number): number {
  const result = Math.sin(value * 12.9898) * 43758.5453;
  return result - Math.floor(result);
}

function createForestTree(scale: number): THREE.Group {
  const tree = new THREE.Group();
  const trunk = mesh(new THREE.CylinderGeometry(0.42, 0.72, 4.6, 12), 0x604632);
  trunk.position.y = 2.25;
  tree.add(trunk);
  for (const [x, y, z, radius, color] of [
    [0, 4.7, 0, 1.8, 0x315f3b],
    [-1.05, 4.35, 0.2, 1.25, 0x3e7547],
    [1.0, 4.55, -0.15, 1.35, 0x376d42],
    [-0.2, 5.65, 0.1, 1.2, 0x477e4a],
  ] as Array<[number, number, number, number, number]>) {
    const foliage = mesh(new THREE.SphereGeometry(radius, 14, 10), color, 0.94);
    foliage.scale.y = 0.86;
    foliage.position.set(x, y, z);
    tree.add(foliage);
  }
  tree.add(createBlobShadow(1.9, 1.3, 0.025));
  tree.scale.setScalar(scale);
  return tree;
}

function createBunny(): THREE.Group {
  const rabbit = new THREE.Group();
  rabbit.name = "fern-bunny-character";
  const fur = 0x9b795e;
  const lightFur = 0xd3b99c;
  const body = mesh(new THREE.SphereGeometry(0.62, 18, 14), fur, 0.95);
  body.scale.set(0.9, 1.05, 1.2);
  body.position.set(0, 0.65, 0.15);
  const chest = mesh(new THREE.SphereGeometry(0.4, 16, 12), lightFur, 0.95);
  chest.scale.set(0.7, 1, 0.45);
  chest.position.set(0, 0.68, -0.48);
  const head = mesh(new THREE.SphereGeometry(0.45, 18, 14), fur, 0.95);
  head.position.set(0, 1.38, -0.22);
  const muzzle = mesh(new THREE.SphereGeometry(0.22, 14, 10), lightFur);
  muzzle.scale.set(1.1, 0.7, 0.8);
  muzzle.position.set(0, 1.29, -0.61);
  const tail = mesh(new THREE.SphereGeometry(0.25, 14, 10), 0xe4d7c5);
  tail.position.set(0, 0.72, 0.82);
  rabbit.add(body, chest, head, muzzle, tail, createBlobShadow(0.7, 0.5, 0.02));

  for (const x of [-0.2, 0.2]) {
    const ear = mesh(new THREE.CapsuleGeometry(0.11, 0.5, 5, 9), fur);
    ear.position.set(x, 1.95, -0.12);
    ear.rotation.z = x * 0.45;
    const inner = mesh(new THREE.CapsuleGeometry(0.045, 0.38, 5, 8), 0xc98782);
    inner.position.set(x, 1.96, -0.23);
    inner.rotation.z = x * 0.45;
    const eye = mesh(new THREE.SphereGeometry(0.072, 10, 8), 0x1d201d);
    eye.position.set(x * 1.35, 1.47, -0.53);
    const glint = mesh(new THREE.SphereGeometry(0.018, 6, 5), 0xffffff);
    glint.position.set(x * 1.35 - 0.012, 1.495, -0.587);
    const brow = mesh(new THREE.CapsuleGeometry(0.013, 0.1, 3, 6), 0x6d503d);
    brow.rotation.z = Math.PI / 2 + x * 0.4;
    brow.position.set(x * 1.3, 1.59, -0.48);
    rabbit.add(ear, inner, eye, glint, brow);
  }
  const nose = mesh(new THREE.SphereGeometry(0.07, 9, 7), 0x5a3532);
  nose.position.set(0, 1.34, -0.79);
  const neckerchief = mesh(
    new THREE.TorusGeometry(0.32, 0.05, 6, 16),
    0x5c94bd,
  );
  neckerchief.name = "fern-ranger-neckerchief";
  neckerchief.rotation.x = Math.PI / 2;
  neckerchief.position.set(0, 1.12, -0.16);
  const leafCharm = mesh(new THREE.SphereGeometry(0.075, 8, 6), 0x78aa59);
  leafCharm.scale.set(0.55, 1, 0.3);
  leafCharm.rotation.z = -0.45;
  leafCharm.position.set(0.12, 1.02, -0.48);
  rabbit.add(
    nose,
    neckerchief,
    leafCharm,
    ...createWhiskers(1.34, -0.76, 0x6d503d),
  );
  rabbit.scale.setScalar(1.12);
  return rabbit;
}

function createHumanSkeletonDisplay(): THREE.Group {
  const skeleton = new THREE.Group();
  const boneColor = 0xeee4c9;
  const skull = mesh(new THREE.SphereGeometry(0.24, 12, 9), boneColor);
  skull.scale.set(0.82, 1, 0.75);
  skull.position.y = 2.45;
  const spine = mesh(new THREE.CapsuleGeometry(0.045, 0.9, 4, 7), boneColor);
  spine.position.y = 1.58;
  const ribs = mesh(
    new THREE.TorusGeometry(0.38, 0.055, 6, 14, Math.PI * 1.7),
    boneColor,
  );
  ribs.rotation.z = 0.15;
  ribs.position.set(0, 1.83, -0.02);
  const hip = mesh(
    new THREE.TorusGeometry(0.3, 0.07, 6, 12, Math.PI),
    boneColor,
  );
  hip.rotation.z = Math.PI;
  hip.position.y = 1.06;
  skeleton.add(skull, spine, ribs, hip);
  for (const side of [-1, 1]) {
    const upperArm = mesh(
      new THREE.CapsuleGeometry(0.045, 0.55, 4, 7),
      boneColor,
    );
    upperArm.position.set(side * 0.47, 1.75, 0);
    upperArm.rotation.z = side * -0.32;
    const lowerArm = mesh(
      new THREE.CapsuleGeometry(0.04, 0.5, 4, 7),
      boneColor,
    );
    lowerArm.position.set(side * 0.7, 1.2, 0);
    lowerArm.rotation.z = side * -0.42;
    const upperLeg = mesh(
      new THREE.CapsuleGeometry(0.055, 0.62, 4, 7),
      boneColor,
    );
    upperLeg.position.set(side * 0.2, 0.63, 0);
    upperLeg.rotation.z = side * -0.08;
    const lowerLeg = mesh(
      new THREE.CapsuleGeometry(0.045, 0.58, 4, 7),
      boneColor,
    );
    lowerLeg.position.set(side * 0.23, 0.05, 0);
    lowerLeg.rotation.z = side * 0.05;
    skeleton.add(upperArm, lowerArm, upperLeg, lowerLeg);
  }
  return skeleton;
}

function createSafariStation(
  kind: "fossil" | "guardian" | "bridge" | "model" | "lab",
  x: number,
  z: number,
): THREE.Group {
  const station = new THREE.Group();
  station.position.set(x, 0, z);
  const beaconMaterial = new THREE.MeshStandardMaterial({
    color: 0xe9cf78,
    emissive: 0x9b7b2b,
    emissiveIntensity: 0.5,
  });
  const beacon = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.22, 0),
    beaconMaterial,
  );
  beacon.position.y = 2.35;
  station.add(beacon);

  if (kind === "fossil") {
    const dig = mesh(new THREE.CylinderGeometry(1.65, 1.8, 0.12, 24), 0xb69a72);
    dig.position.y = 0.06;
    station.add(dig);
    for (let index = 0; index < 7; index += 1) {
      const bone = mesh(new THREE.CapsuleGeometry(0.06, 0.45, 4, 7), 0xe8ddc5);
      bone.rotation.z = 0.8 + index * 0.34;
      bone.position.set(-0.8 + index * 0.24, 0.18, Math.sin(index) * 0.55);
      station.add(bone);
    }
  } else if (kind === "guardian") {
    const arch = mesh(
      new THREE.TorusGeometry(1.15, 0.18, 9, 24, Math.PI),
      0x81745d,
    );
    arch.rotation.z = Math.PI;
    arch.position.y = 1.2;
    const nest = mesh(new THREE.TorusGeometry(0.62, 0.2, 7, 18), 0x8c6945);
    nest.rotation.x = Math.PI / 2;
    nest.position.y = 0.22;
    station.add(arch, nest);
  } else if (kind === "bridge") {
    for (let index = -3; index <= 3; index += 1) {
      const plank = mesh(new THREE.BoxGeometry(0.65, 0.13, 1.7), 0x7d5a38);
      plank.position.set(index * 0.58, 0.24 + Math.abs(index) * 0.035, 0);
      station.add(plank);
    }
    const supports = mesh(
      new THREE.CylinderGeometry(0.12, 0.15, 2.2, 8),
      0x66503b,
    );
    supports.rotation.z = Math.PI / 2;
    supports.position.y = 0.1;
    station.add(supports);
  } else if (kind === "model") {
    const table = mesh(new THREE.BoxGeometry(2.5, 0.16, 1.2), 0x71533a);
    table.position.y = 0.9;
    station.add(table);
    for (const side of [-1, 1]) {
      const leg = mesh(new THREE.CylinderGeometry(0.1, 0.12, 0.9, 8), 0x60442f);
      leg.position.set(side * 0.95, 0.45, 0);
      station.add(leg);
    }
    const model = createHumanSkeletonDisplay();
    model.scale.setScalar(0.48);
    model.position.y = 1.02;
    station.add(model);
  } else {
    const canopy = mesh(new THREE.ConeGeometry(1.65, 1.25, 4), 0xc7a45e);
    canopy.rotation.y = Math.PI / 4;
    canopy.position.y = 1.55;
    const bench = mesh(new THREE.BoxGeometry(2.2, 0.15, 0.8), 0x76543a);
    bench.position.y = 0.72;
    const ruler = mesh(new THREE.BoxGeometry(1.65, 0.04, 0.16), 0xe7d19a);
    ruler.position.set(0, 0.83, 0);
    station.add(canopy, bench, ruler);
  }
  return station;
}

function createTree(
  x: number,
  z: number,
  scale = 1,
  crownColor = 0x3b9b5f,
): THREE.Group {
  const tree = new THREE.Group();
  const trunk = mesh(new THREE.CylinderGeometry(0.31, 0.5, 2.65, 9), 0x765037);
  trunk.position.y = 1.3;
  trunk.rotation.z = 0.035;
  const crown = mesh(new THREE.IcosahedronGeometry(1.25, 1), crownColor);
  crown.scale.set(1.15, 1.3, 1.05);
  crown.position.y = 3.15;
  const crownSide = mesh(new THREE.IcosahedronGeometry(0.82, 1), crownColor);
  crownSide.position.set(0.72, 3.05, 0.18);
  const crownLight = mesh(new THREE.IcosahedronGeometry(0.68, 1), 0x58ae5e);
  crownLight.position.set(-0.65, 3.42, -0.15);
  tree.add(
    trunk,
    crown,
    crownSide,
    crownLight,
    createBlobShadow(1.6, 1.05, 0.025),
  );

  for (let index = 0; index < 4; index += 1) {
    const fruit = mesh(
      new THREE.SphereGeometry(0.13, 8, 6),
      index % 2 === 0 ? 0xffb43f : 0xf36b5d,
    );
    fruit.position.set(
      -0.72 + index * 0.42,
      3.05 + (index % 2) * 0.48,
      -0.92 + (index % 3) * 0.25,
    );
    tree.add(fruit);
  }
  tree.position.set(x, 0, z);
  tree.scale.setScalar(scale);
  return tree;
}

function createFlower(color: number): THREE.Group {
  const flower = new THREE.Group();
  const stem = mesh(new THREE.CylinderGeometry(0.018, 0.025, 0.3, 5), 0x3d8b4e);
  stem.position.y = 0.15;
  const centre = mesh(new THREE.SphereGeometry(0.055, 7, 5), 0xffd653);
  centre.position.y = 0.35;
  flower.add(stem, centre);
  for (let index = 0; index < 5; index += 1) {
    const petal = mesh(new THREE.SphereGeometry(0.065, 7, 5), color);
    const angle = (index / 5) * Math.PI * 2;
    petal.scale.set(1.35, 0.45, 0.9);
    petal.position.set(Math.sin(angle) * 0.095, 0.35, Math.cos(angle) * 0.095);
    flower.add(petal);
  }
  return flower;
}

function createBush(x: number, z: number, color: number): THREE.Group {
  const bush = new THREE.Group();
  for (const [dx, dy, dz, scale] of [
    [0, 0.52, 0, 0.78],
    [-0.5, 0.4, 0.05, 0.57],
    [0.48, 0.4, 0.12, 0.6],
    [0.04, 0.38, -0.45, 0.55],
  ] as Array<[number, number, number, number]>) {
    const clump = mesh(new THREE.IcosahedronGeometry(scale, 1), color);
    clump.position.set(dx, dy, dz);
    bush.add(clump);
  }
  bush.position.set(x, 0, z);
  return bush;
}

function createPortal(
  x: number,
  z: number,
  rotation: number,
  stoneColor: number,
  glowColor: number,
  symbol: "bone" | "muscle" | "shell" | "leaf",
): THREE.Group {
  const portal = new THREE.Group();
  const pillarGeometry = new THREE.CylinderGeometry(0.38, 0.56, 3.45, 10);
  const left = mesh(pillarGeometry, stoneColor);
  const right = mesh(pillarGeometry, stoneColor);
  left.position.set(-1.55, 1.72, 0);
  right.position.set(1.55, 1.72, 0);
  const crown = mesh(
    new THREE.TorusGeometry(1.55, 0.34, 9, 24, Math.PI),
    stoneColor,
  );
  crown.rotation.z = Math.PI;
  crown.position.y = 3.42;
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: glowColor,
    transparent: true,
    opacity: 0.58,
    side: THREE.DoubleSide,
  });
  const glow = new THREE.Mesh(new THREE.CircleGeometry(1.28, 28), glowMaterial);
  glow.position.set(0, 1.7, 0.08);
  const badge = createPortalBadge(symbol, glowColor);
  badge.position.set(0, 3.67, 0.18);
  portal.add(left, right, crown, glow, badge);
  portal.position.set(x, 0, z);
  portal.rotation.y = rotation;
  return portal;
}

function createPortalBadge(
  symbol: "bone" | "muscle" | "shell" | "leaf",
  color: number,
): THREE.Group {
  const badge = new THREE.Group();
  const disc = mesh(new THREE.CylinderGeometry(0.46, 0.46, 0.13, 18), 0xfff4c8);
  disc.rotation.x = Math.PI / 2;
  let icon: THREE.Mesh;
  if (symbol === "bone") {
    icon = mesh(new THREE.CapsuleGeometry(0.09, 0.4, 4, 7), color);
    icon.rotation.z = Math.PI / 2;
  } else if (symbol === "muscle") {
    icon = mesh(
      new THREE.TorusGeometry(0.22, 0.08, 6, 12, Math.PI * 1.4),
      color,
    );
    icon.rotation.z = -0.7;
  } else if (symbol === "shell") {
    icon = mesh(new THREE.SphereGeometry(0.27, 10, 7), color);
    icon.scale.set(1, 0.35, 1);
  } else {
    icon = mesh(new THREE.SphereGeometry(0.26, 10, 7), color);
    icon.scale.set(0.58, 1, 0.24);
    icon.rotation.z = -0.55;
  }
  icon.position.z = 0.11;
  badge.add(disc, icon);
  return badge;
}

function createLantern(x: number, z: number): THREE.Group {
  const lantern = new THREE.Group();
  const post = mesh(new THREE.CylinderGeometry(0.055, 0.08, 1.25, 7), 0x4f564d);
  post.position.y = 0.62;
  const cap = mesh(new THREE.ConeGeometry(0.24, 0.22, 6), 0x58496d);
  cap.position.y = 1.48;
  const lightMaterial = new THREE.MeshStandardMaterial({
    color: 0xffe37b,
    emissive: 0xffa83d,
    emissiveIntensity: 1.1,
  });
  const light = new THREE.Mesh(
    new THREE.SphereGeometry(0.16, 9, 7),
    lightMaterial,
  );
  light.position.y = 1.3;
  lantern.add(post, cap, light);
  lantern.position.set(x, 0, z);
  return lantern;
}

function createCloud(): THREE.Group {
  const cloud = new THREE.Group();
  for (const [x, y, scale] of [
    [-0.9, 0, 0.75],
    [0, 0.25, 1.05],
    [0.95, 0, 0.8],
    [0.2, -0.12, 0.85],
  ] as Array<[number, number, number]>) {
    const puff = mesh(new THREE.IcosahedronGeometry(0.9, 1), 0xf7fbef);
    puff.scale.set(scale * 1.25, scale * 0.75, scale);
    puff.position.set(x, y, 0);
    cloud.add(puff);
  }
  return cloud;
}

function createWhiskers(y: number, z: number, color: number): THREE.Mesh[] {
  const whiskers: THREE.Mesh[] = [];
  for (const side of [-1, 1]) {
    for (let index = -1; index <= 1; index += 1) {
      const whisker = mesh(
        new THREE.CylinderGeometry(0.006, 0.006, 0.3, 4),
        color,
      );
      whisker.name = "character-whisker";
      whisker.rotation.z = Math.PI / 2 + side * index * 0.12;
      whisker.position.set(side * 0.24, y + index * 0.035, z);
      whiskers.push(whisker);
    }
  }
  return whiskers;
}

function createBlobShadow(width: number, depth: number, y: number): THREE.Mesh {
  const shadowMaterial = new THREE.MeshBasicMaterial({
    color: 0x1e513d,
    transparent: true,
    opacity: 0.2,
    depthWrite: false,
  });
  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(1, 18),
    shadowMaterial,
  );
  shadow.scale.set(width, depth, 1);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = y;
  shadow.renderOrder = -1;
  return shadow;
}

function createButterfly(color: number): THREE.Group {
  const butterfly = new THREE.Group();
  const body = mesh(new THREE.CapsuleGeometry(0.035, 0.18, 3, 5), 0x4e3b39);
  body.rotation.x = Math.PI / 2;
  for (const side of [-1, 1]) {
    const wing = mesh(new THREE.SphereGeometry(0.16, 7, 5), color);
    wing.scale.set(1.1, 0.16, 1.45);
    wing.position.set(side * 0.15, 0.06, 0);
    wing.rotation.z = side * 0.35;
    butterfly.add(wing);
  }
  butterfly.add(body);
  return butterfly;
}
